'use strict';
/* ==========================================================================
   levels/video.js — step 1, THE GROUND IS MOVING: the interactive video (shared/engine/v2/core/player.js) with one
   scene per script segment (content/script.json s1–s5; each segment's "visual" is the storyboard).

   Every scene is a PURE FUNCTION of the part time t: beats are keyed to the dialogue through ctx.L / ctx.W (via
   cues(), which keeps cues from other parts finite), so the choreography follows whatever the voice timing is
   (text timing now, the recorded clips later), and the player can seek, replay and run at 25×.

   Honesty (DESIGN.md §4): every number on screen comes from core-local/plates.js (Plates) or from the real data in
   assets/data (quakes.json: USGS M5+ 2025; coast.json: Natural Earth; plates.json: PB2002 + its credit line), never a
   literal. The two exceptions are drawing geometry, not facts: the Earth's layer radii (PREM, facts-ground.md §7) and
   the length of a school ruler (30 cm, the storyboard's ruler).

   Cost (docs/WEBSITE.md §8.1): static art is baked once into off-screen layers (layer(): the survey backdrop, the
   world map, the quake dots per month, the globe halves, the Earth cross-section, the map scraps), registered with
   the engine's off-screen budget (OFF) so they are evicted like sprites; a frame blits them and draws only what moves.
   No per-frame shadowOn(); paper pieces use the engine's cached sprites.

   Staging (like Energy's video_kit): a chapter card tears away at each segment start and the next one slides in at
   its end; Leo (left) and Dr. Quinn (right) stand full size for dialogue and shrink to bottom-corner insets when the
   action needs the screen; their caption bubbles sit next to them, away from the action.
   Characters: Puppet 'quinn' (poses quinn, quinn_point) and 'leo' (leo_cross, leo_point); core-local/hooks.js.
   QA: tools/qa_video.js (every beat, every branch, desktop + iPhone 12 portrait); window.GroundVideo (debug).
   ========================================================================== */
(function () {
  const S = Player.scenes;
  const HANDF = "'Caveat Brush'", TYPE = "'Special Elite'";
  const COL = GroundLook.COL;
  const OUT = 'rgba(37,60,90,0.9)';
  const LAND = '#eadcb4', LAND_EDGE = 'rgba(43,36,32,0.62)', OCEAN = '#c9dcd9', PLATE_INK = '#b8402c', QUAKE = 'rgba(214,64,40,0.82)';
  const RULER_CM = 30;                          // a school ruler (the storyboard's "one ruler")
  // Earth's layers for the cross-section (PREM, facts-ground.md §7: radius 6,371 km; core–mantle boundary 2,891 km deep;
  // inner-core radius ~1,221 km). Drawing proportions only; the crust is drawn much thicker than it is (labelled "really much thinner").
  const EARTH_KM = 6371, CMB_KM = 6371 - 2891, ICB_KM = 1221;
  const LOW = () => !!(window.Quality && Quality.tier === 'low');

  /* ---------------- small helpers ---------------- */
  const fin = x => (x === Infinity ? 1e6 : x === -Infinity ? -1e6 : x);
  const cues = ctx => ({ L: id => fin(ctx.L(id)), E: id => fin(ctx.E(id)), W: (id, w, n = 1) => fin(ctx.W(id, w, n)) });
  const win = (t, a, b, ramp = 0.35) => Math.min(E.inOutSine(P(t, a, a + ramp)), 1 - E.inOutSine(P(t, b, b + ramp)));
  const fmtInt = n => Math.round(n).toLocaleString('en-US');
  const fmtNum = (x, d = 1) => { const v = +(+x).toFixed(d); return Number.isInteger(v) ? fmtInt(v) : v.toLocaleString('en-US', { maximumFractionDigits: d }); };
  const segIndex = seg => Game.content.script.segments.indexOf(seg);
  const lastKind = seg => (seg.after && seg.after.length ? 'after' : seg.question ? 'reply' : 'lines');
  const stepTitle = id => { const s = (Game.lesson && Game.lesson.steps || []).find(x => x.id === id); return s ? s.title : id; };
  /* a line id of this part (reply parts differ per answer) */
  const replyId = part => (part.lines[0] ? part.lines[0].id : '');

  /* ======================================================================
     Layers: static art baked once into an off-screen canvas (registered with the engine's OFF budget)
     layer(key, x, y, w, h, draw, {zoom, ver, noDraw}) — draw() paints the rect (x, y, w, h) in the CALLER's
     coordinates; the bake is at DS × zoom backing px per unit. ver: re-bake when it changes (the quake months).
     ====================================================================== */
  const LY = new Map();
  function layer(key, x, y, w, h, draw, o = {}) {
    const res = +Math.min(3, Math.max(0.3, DS * (o.zoom || 1))).toFixed(3), ver = o.ver ?? 0;
    let e = LY.get(key);
    if (!e || e.dead || e.res !== res || e.ver !== ver || e.x !== x || e.y !== y || e.w !== w || e.h !== h) {
      if (e && !e.dead) OFF.drop(e);
      LY.delete(key);
      const pw = Math.ceil(w * res), ph = Math.ceil(h * res);
      if (!FX.sprites || typeof _bake !== 'function' || !OFF.room(pw * ph * 4)) { if (!o.noDraw) draw(); return false; }
      const c = _bake(pw, ph, cx => cx.setTransform(res, 0, 0, res, -x * res, -y * res), draw);
      e = OFF.add(c, { res, ver, x, y, w, h });
      const me = e; e.drop = () => { if (LY.get(key) === me) LY.delete(key); };
      LY.set(key, e);
    }
    e.frame = OFF.frame;
    if (!o.noDraw) g.drawImage(e.c, x, y, e.c.width / res, e.c.height / res);
    return true;
  }

  /* ======================================================================
     Geography (assets/data): coastlines → land rings, plate boundaries, quakes; equirectangular + orthographic
     ====================================================================== */
  let GEO = null;
  function geo() {
    if (GEO) return GEO;
    const Cn = Game.content || {}, C = Cn.coast, Q = Cn.quakes, B = Cn.plates;
    if (!C || !Q || !B || typeof Path2D === 'undefined') return null;
    const lines = C.lines.map(l => { const p = []; for (let i = 0; i < l.length; i += 2) p.push([l[i], l[i + 1]]); return p; });
    const eq = (a, b) => a[0] === b[0] && a[1] === b[1];
    const isClosed = l => l.length > 3 && eq(l[0], l[l.length - 1]);
    /* chain a set of open lines by their exact end points (either direction) → one polyline */
    function chain(ids) {
      const left = new Set(ids.slice(1));
      let ch = lines[ids[0]].slice(), grew = true;
      while (grew) {
        grew = false;
        for (const i of left) {
          const a = lines[i], e = ch[ch.length - 1], s0 = ch[0];
          if (eq(a[0], e)) ch = ch.concat(a.slice(1));
          else if (eq(a[a.length - 1], e)) ch = ch.concat(a.slice(0, -1).reverse());
          else if (eq(a[a.length - 1], s0)) ch = a.slice(0, -1).concat(ch);
          else if (eq(a[0], s0)) ch = a.slice(1).reverse().concat(ch);
          else continue;
          left.delete(i); grew = true;
        }
      }
      return { pts: ch, used: ids.filter(i => !left.has(i)) };
    }
    const openIds = lines.map((l, i) => i).filter(i => !isClosed(lines[i]));
    const rings = [];                 // {pts, ids}
    lines.forEach((l, i) => { if (isClosed(l)) rings.push({ pts: l, ids: [i] }); });
    const done = new Set();
    for (const i of openIds) {
      if (done.has(i)) continue;
      const { pts, used } = chain([i, ...openIds.filter(j => j !== i && !done.has(j))]);
      used.forEach(j => done.add(j));
      const a = pts[0], b = pts[pts.length - 1];
      // a chain closes if its ends meet, or both end on the same side of the date line (Afro-Eurasia)
      if (eq(a, b) || (Math.abs(a[0]) === 180 && Math.abs(b[0]) === 180 && a[0] === b[0])) rings.push({ pts, ids: used });
    }
    const box = pts => { let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9; for (const [x, y] of pts) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; } return { x0, x1, y0, y1, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 }; };
    rings.forEach(r => { r.box = box(r.pts); });
    const pathOf = (list, close = true) => { const p = new Path2D(); for (const pts of list) { pts.forEach((q, k) => (k ? p.lineTo(q[0], -q[1]) : p.moveTo(q[0], -q[1]))); if (close) p.closePath(); } return p; };
    const coast = pathOf(lines, false);
    const land = pathOf(rings.filter(r => r.box.y1 > -60).map(r => r.pts));
    // the pieces Pangaea moves (identified from the data, not by index): the Americas ring's lines split into
    // South America (lines reaching south of 20° S) and North America; Greenland = the closed ring centred ~ 40° W, 70° N
    const amer = rings.find(r => r.ids.length > 1 && r.box.x0 < -150 && r.box.x1 < -20 && r.box.y0 < -40);
    let SA = null, NA = null;
    if (amer) {
      const sa = amer.ids.filter(i => Math.min(...lines[i].map(q => q[1])) < -20), na = amer.ids.filter(i => !sa.includes(i));
      if (sa.length && na.length) { SA = chain(sa).pts; NA = chain(na).pts; }
    }
    // every other ring on the Americas' side (Greenland, the Arctic islands, the Caribbean…) rides with North America
    const west = rings.filter(r => r !== amer && r.box.cx < -25 && r.box.y1 > -60);
    const moving = new Set([amer, ...west].filter(Boolean));
    const pieces = { SA: SA ? [SA] : [], NA: (NA ? [NA] : []).concat(west.map(r => r.pts)) };
    const rest = pathOf(rings.filter(r => !moving.has(r) && r.box.y1 > -60).map(r => r.pts));
    // plate boundaries (PB2002): polylines, broken where they cross the date line
    const plateLines = [];
    for (const l of B.lines) {
      let cur = [];
      for (let i = 0; i < l.p.length; i += 2) {
        const q = [l.p[i], l.p[i + 1]];
        if (cur.length && Math.abs(q[0] - cur[cur.length - 1][0]) > 180) { if (cur.length > 1) plateLines.push({ n: l.n, t: l.t, pts: cur }); cur = []; }
        cur.push(q);
      }
      if (cur.length > 1) plateLines.push({ n: l.n, t: l.t, pts: cur });
    }
    plateLines.forEach(pl => { let L = 0; for (let i = 1; i < pl.pts.length; i++) L += Math.hypot(pl.pts[i][0] - pl.pts[i - 1][0], pl.pts[i][1] - pl.pts[i - 1][1]); pl.len = L; });
    const plates = pathOf(plateLines.map(p => p.pts), false);
    // quakes: [lon, lat, mag, depthKm, dayOfYear], in time order; month boundaries from the data's own year
    const year = Q.year || 2025, starts = [];
    for (let m = 0; m <= 12; m++) starts.push(Math.round((Date.UTC(year, m, 1) - Date.UTC(year, 0, 1)) / 864e5));   // days before month m
    const q = Q.q;
    const monthOf = d => { let m = 0; while (m < 11 && d > starts[m + 1]) m++; return m; };
    const byMonth = Array.from({ length: 12 }, () => []);
    q.forEach(r => byMonth[monthOf(r[4])].push(r));
    GEO = { lines, rings, coast, land, rest, pieces, plateLines, plates, q, byMonth, starts, year, Q, B, C, pathOf };
    return GEO;
  }
  const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const QUAKE_R = mag => 1.5 + Math.max(0, mag - 5) * 1.55;     // dot radius, screen px at the world map's scale

  /* the nearest plate-boundary point to (lon, lat), great-circle km (for the Florida line; the label is Plates') */
  function nearestEdge(lon, lat) {
    const G = geo(); if (!G) return null;
    const R = 6371, rad = Math.PI / 180;
    const hav = (a, b) => { const d1 = (b[1] - a[1]) * rad, d2 = (b[0] - a[0]) * rad; const h = Math.sin(d1 / 2) ** 2 + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(d2 / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(h)); };
    let best = null;
    for (const pl of G.plateLines) for (let i = 1; i < pl.pts.length; i++) {
      const a = pl.pts[i - 1], b = pl.pts[i];
      for (let k = 0; k <= 8; k++) { const p = [lerp(a[0], b[0], k / 8), lerp(a[1], b[1], k / 8)], d = hav([lon, lat], p); if (!best || d < best.km) best = { km: d, lon: p[0], lat: p[1] }; }
    }
    return best;
  }

  /* ---------- equirectangular views ----------
     v = {x, y (box centre), w, h, s (px per degree), lon0, lat0 (view centre)} */
  const worldView = (x, y, w, h) => ({ x, y, w, h, s: Math.min((w - 48) / 360, (h - 48) / 144), lon0: 0, lat0: 12 });
  const vX = (v, lon) => v.x + (lon - v.lon0) * v.s;
  const vY = (v, lat) => v.y - (lat - v.lat0) * v.s;
  function clipBox(v, inset = 16) { g.beginPath(); g.rect(v.x - v.w / 2 + inset, v.y - v.h / 2 + inset, v.w - 2 * inset, v.h - 2 * inset); g.clip(); }
  function intoView(v) { g.translate(v.x - v.lon0 * v.s, v.y + v.lat0 * v.s); g.scale(v.s, v.s); }
  /* ocean tint + land + coast (+ plates) of a view, clipped to its box */
  function geoBase(v, { plates = 0, ocean = true, land = true, erase = null, coastW = 1.5 } = {}) {
    const G = geo(); if (!G) return;
    g.save(); clipBox(v);
    if (ocean) { g.fillStyle = 'rgba(79,127,163,0.16)'; g.fillRect(v.x - v.w / 2, v.y - v.h / 2, v.w, v.h); }
    intoView(v);
    if (land) { g.fillStyle = LAND; g.fill(G.land); }
    if (erase) { g.fillStyle = erase.color; g.fill(erase.path); }
    g.lineJoin = 'round'; g.lineWidth = coastW / v.s; g.strokeStyle = LAND_EDGE; g.stroke(G.coast);
    if (plates > 0) { g.globalAlpha = plates; g.lineWidth = 2.4 / v.s; g.strokeStyle = PLATE_INK; g.stroke(G.plates); }
    g.restore();
  }
  /* quake dots of list `rows`, radius × k; pop(r) → 0..1 per dot (null = all full) */
  function quakeDots(v, rows, { k = 1, alpha = 1, pop: popFn = null } = {}) {
    if (!rows || !rows.length) return;
    g.save(); clipBox(v); g.globalAlpha *= alpha; g.fillStyle = QUAKE;
    g.beginPath();
    for (const r of rows) {
      const s = popFn ? popFn(r) : 1; if (s <= 0.01) continue;
      const x = vX(v, r[0]), y = vY(v, r[1]), rr = QUAKE_R(r[2]) * k * s;
      g.moveTo(x + rr, y); g.arc(x, y, rr, 0, TAU);
    }
    g.fill();
    g.restore();
  }
  /* plate lines drawn on over p (every line grows from its start at once, like ink joining the dots) */
  function plateReveal(v, p, alpha = 1) {
    const G = geo(); if (!G || p <= 0) return;
    g.save(); clipBox(v); intoView(v);
    g.globalAlpha *= alpha; g.lineWidth = 2.6 / v.s; g.strokeStyle = PLATE_INK; g.lineJoin = 'round'; g.lineCap = 'round';
    if (p >= 1) { g.stroke(G.plates); g.restore(); return; }
    g.beginPath();
    for (const pl of G.plateLines) {
      let left = pl.len * p, prev = pl.pts[0]; g.moveTo(prev[0], -prev[1]);
      for (let i = 1; i < pl.pts.length && left > 0; i++) {
        const q = pl.pts[i], d = Math.hypot(q[0] - prev[0], q[1] - prev[1]);
        if (d <= left) { g.lineTo(q[0], -q[1]); left -= d; } else { const k = left / d; g.lineTo(lerp(prev[0], q[0], k), -lerp(prev[1], q[1], k)); left = 0; }
        prev = q;
      }
    }
    g.stroke(); g.restore();
  }
  /* the plate lines whose PB2002 name matches (e.g. /^(IN-EU|EU-IN)$/), in a view */
  function platesNamed(v, re, { w = 5, color = PLATE_INK, alpha = 1, dash = null } = {}) {
    const G = geo(); if (!G) return;
    g.save(); clipBox(v); g.globalAlpha *= alpha; g.strokeStyle = color; g.lineWidth = w; g.lineJoin = 'round'; g.lineCap = 'round';
    if (dash) g.setLineDash(dash);
    g.beginPath();
    for (const pl of G.plateLines) if (re.test(pl.n)) pl.pts.forEach((q, i) => (i ? g.lineTo(vX(v, q[0]), vY(v, q[1])) : g.moveTo(vX(v, q[0]), vY(v, q[1]))));
    g.stroke(); g.restore();
  }

  /* ---------- the globe (orthographic, centred on the Atlantic) ---------- */
  const GLOBE = { lon0: -38, lat0: 18 };
  function ortho(lon, lat, lon0 = GLOBE.lon0, lat0 = GLOBE.lat0) {
    const r = Math.PI / 180, f = lat * r, l = (lon - lon0) * r, f0 = lat0 * r;
    const cosc = Math.sin(f0) * Math.sin(f) + Math.cos(f0) * Math.cos(f) * Math.cos(l);
    let x = Math.cos(f) * Math.sin(l), y = Math.cos(f0) * Math.sin(f) - Math.sin(f0) * Math.cos(f) * Math.cos(l);
    if (cosc < 0) { const k = Math.hypot(x, y) || 1; x /= k; y /= k; }       // behind: pushed onto the limb (fills stay closed)
    return { x, y: -y, front: cosc >= 0 };
  }
  /* the globe's land, exactly: an equirectangular land mask (the coast rings filled, 4 px per degree) sampled per
     pixel through the inverse orthographic projection → one canvas per half (W = the Americas side, E = the rest),
     textured with kraft paper. Baked once per size (inside the globe layers). */
  const MASK_K = 4, MASK_W = 360 * MASK_K, MASK_H = 180 * MASK_K;
  let MASKS = null;
  const westRing = r => r.box.cx < -25 && r.box.x1 < -10;
  function landMasks() {
    if (MASKS) return MASKS;
    const G = geo(); if (!G) return null;
    const out = {};
    for (const which of ['W', 'E']) {
      const c = document.createElement('canvas'); c.width = MASK_W; c.height = MASK_H;
      const x = c.getContext('2d'); x.setTransform(MASK_K, 0, 0, MASK_K, MASK_W / 2, MASK_H / 2);
      x.fillStyle = '#000'; x.fill(G.pathOf(G.rings.filter(r => (which === 'W') === westRing(r)).map(r => r.pts)));
      const d = x.getImageData(0, 0, MASK_W, MASK_H).data, a = new Uint8Array(MASK_W * MASK_H);
      for (let i = 0; i < a.length; i++) a[i] = d[i * 4 + 3];
      out[which] = a; c.width = c.height = 0;
    }
    return (MASKS = out);
  }
  const _globeLand = new Map();
  function globeLand(N) {
    if (_globeLand.has(N)) return _globeLand.get(N);
    const M = landMasks(); if (!M) return null;
    const mk = () => { const c = document.createElement('canvas'); c.width = c.height = N; return c; };
    const cW = mk(), cE = mk(), xW = cW.getContext('2d'), xE = cE.getContext('2d');
    const iW = xW.createImageData(N, N), iE = xE.createImageData(N, N), dW = iW.data, dE = iE.data;
    const r = Math.PI / 180, f0 = GLOBE.lat0 * r, sf0 = Math.sin(f0), cf0 = Math.cos(f0);
    for (let j = 0; j < N; j++) {
      const v = -((j + 0.5) / N * 2 - 1);
      for (let i = 0; i < N; i++) {
        const u = (i + 0.5) / N * 2 - 1, rho = Math.hypot(u, v);
        if (rho > 1) continue;
        const cc = Math.asin(Math.min(1, rho)), sc = Math.sin(cc), ccos = Math.cos(cc);
        const lat = rho < 1e-9 ? GLOBE.lat0 : Math.asin(ccos * sf0 + v * sc * cf0 / rho) / r;
        let lon = GLOBE.lon0 + Math.atan2(u * sc, rho * ccos * cf0 - v * sc * sf0) / r;
        lon = ((lon + 540) % 360) - 180;
        const mx = Math.min(MASK_W - 1, Math.max(0, Math.floor((lon + 180) * MASK_K))), my = Math.min(MASK_H - 1, Math.max(0, Math.floor((90 - lat) * MASK_K)));
        const k = my * MASK_W + mx, o = (j * N + i) * 4;
        if (M.W[k]) { dW[o] = dW[o + 1] = dW[o + 2] = 255; dW[o + 3] = M.W[k]; }
        if (M.E[k]) { dE[o] = dE[o + 1] = dE[o + 2] = 255; dE[o + 3] = M.E[k]; }
      }
    }
    xW.putImageData(iW, 0, 0); xE.putImageData(iE, 0, 0);
    const res = { W: cW, E: cE };
    if (_globeLand.size > 3) { for (const v of _globeLand.values()) { v.W.width = v.W.height = v.E.width = v.E.height = 0; } _globeLand.clear(); }
    _globeLand.set(N, res);
    return res;
  }
  let GLOBEP = null;
  function globePaths() {
    if (GLOBEP) return GLOBEP;
    const G = geo(); if (!G) return null;
    const W = null, Ea = null, sW = new Path2D(), sE = new Path2D(), grat = new Path2D(), ridge = new Path2D();
    // coast strokes: front side only
    for (const l of G.lines) {
      const cx = l.reduce((a, q) => a + q[0], 0) / l.length, S_ = cx < -25 ? sW : sE;
      let pen = false;
      for (const q of l) { const p = ortho(q[0], q[1]); if (!p.front) { pen = false; continue; } pen ? S_.lineTo(p.x, p.y) : S_.moveTo(p.x, p.y); pen = true; }
    }
    for (let lon = -180; lon < 180; lon += 30) { let pen = false; for (let lat = -90; lat <= 90; lat += 3) { const p = ortho(lon, lat); if (!p.front) { pen = false; continue; } pen ? grat.lineTo(p.x, p.y) : grat.moveTo(p.x, p.y); pen = true; } }
    for (let lat = -60; lat <= 60; lat += 30) { let pen = false; for (let lon = -180; lon <= 180; lon += 3) { const p = ortho(lon, lat); if (!p.front) { pen = false; continue; } pen ? grat.lineTo(p.x, p.y) : grat.moveTo(p.x, p.y); pen = true; } }
    // the Mid-Atlantic Ridge: the PB2002 boundaries between the plates on the two sides of the Atlantic
    const MAR = /^(NA-EU|EU-NA|SA-AF|AF-SA|NA-AF|AF-NA)$/;
    for (const pl of G.plateLines) if (MAR.test(pl.n)) { let pen = false; for (const q of pl.pts) { const p = ortho(q[0], q[1]); if (!p.front) { pen = false; continue; } pen ? ridge.lineTo(p.x, p.y) : ridge.moveTo(p.x, p.y); pen = true; } }
    GLOBEP = { W, E: Ea, sW, sE, grat, ridge };
    return GLOBEP;
  }
  /* the globe at (x, y) radius R; apart = px the two sides have drifted (Americas left, Africa/Europe right) */
  function globe(x, y, R, { apart = 0, ridge = 0, alpha = 1 } = {}) {
    const GP = globePaths(); if (!GP || alpha <= 0) return;
    const pad = 10, key = 'globe' + R;
    g.save(); g.globalAlpha *= alpha; g.translate(x, y);
    layer(key + 'base', -R - pad, -R - pad, 2 * R + 2 * pad, 2 * R + 2 * pad, () => {
      paperCircle(0, 0, R + 6, PAL.white, { seed: 61, depth: 1.6, boil: false });
      g.beginPath(); g.arc(0, 0, R, 0, TAU); g.fillStyle = paperTile(COL.ocean, 3); g.fill();
      g.save(); g.scale(R, R); g.lineWidth = 1.2 / R; g.strokeStyle = 'rgba(251,245,230,0.35)'; g.stroke(GP.grat); g.restore();
    });
    const half = (which, dx) => {
      g.save(); g.beginPath(); g.arc(0, 0, R - 1, 0, TAU); g.clip(); g.translate(dx, 0);
      layer(key + which, -R - pad, -R - pad, 2 * R + 2 * pad, 2 * R + 2 * pad, () => {
        // the land (exact, per pixel) tinted with kraft paper, then the coast ink
        const res = Math.abs(g.getTransform().a), N = Math.ceil(2 * R * res), L = globeLand(N);
        if (L) {
          const c = document.createElement('canvas'); c.width = c.height = N;
          const x = c.getContext('2d'); x.drawImage(L[which], 0, 0);
          x.globalCompositeOperation = 'source-in'; x.fillStyle = which === 'W' ? '#dcc08a' : '#d6bb86'; x.fillRect(0, 0, N, N);
          x.globalCompositeOperation = 'multiply'; x.globalAlpha = 0.5; x.fillStyle = paperTile('#ffffff', 4, 'tex_kraft'); x.fillRect(0, 0, N, N);
          x.globalCompositeOperation = 'destination-in'; x.globalAlpha = 1; x.drawImage(L[which], 0, 0);
          g.drawImage(c, -R, -R, 2 * R, 2 * R);
          c.width = c.height = 0;
        }
        g.save(); g.scale(R, R);
        g.lineWidth = 1.6 / R; g.strokeStyle = 'rgba(43,36,32,0.7)'; g.lineJoin = 'round'; g.stroke(which === 'W' ? GP.sW : GP.sE);
        g.restore();
      });
      g.restore();
    };
    half('W', -apart / 2); half('E', apart / 2);
    if (ridge > 0) { g.save(); g.scale(R, R); g.globalAlpha *= ridge; g.setLineDash([6 / R, 5 / R]); g.lineWidth = 3.2 / R; g.strokeStyle = PLATE_INK; g.stroke(GP.ridge); g.restore(); }
    // a soft terminator for roundness (skipped on the low tier)
    if (!LOW()) {
      const gr = g.createRadialGradient(-R * 0.35, -R * 0.4, R * 0.2, 0, 0, R * 1.02);
      gr.addColorStop(0, 'rgba(255,250,235,0.18)'); gr.addColorStop(0.7, 'rgba(0,0,0,0)'); gr.addColorStop(1, 'rgba(20,24,40,0.28)');
      g.beginPath(); g.arc(0, 0, R, 0, TAU); g.fillStyle = gr; g.fill();
    }
    g.restore();
  }
  const globePt = (lon, lat, x, y, R) => { const p = ortho(lon, lat); return [x + p.x * R, y + p.y * R]; };

  /* ======================================================================
     Backdrops (baked)
     ====================================================================== */
  function surveyBackdrop(seed = 3) {
    layer('bg_survey' + seed, 0, 0, 1920, 1080, () => {
      g.fillStyle = paperTile(PAL.kraft, 23, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
      GroundLook.survey(960, 520, 1880, 1040, { seed, depth: 1.4, contour: 0.7 });
    });
  }
  function spaceBackdrop() {
    layer('bg_space', 0, 0, 1920, 1080, () => {
      g.fillStyle = paperTile(PAL.navy, 9, IMG.tex_stars ? 'tex_stars' : 'tex_paper'); g.fillRect(0, 0, 1920, 1080);
      const r = rng(77);
      g.fillStyle = 'rgba(251,245,230,0.75)';
      for (let i = 0; i < 90; i++) { g.beginPath(); g.arc(r() * 1920, r() * 1080, 0.8 + r() * 1.8, 0, TAU); g.fill(); }
    });
  }
  /* the Florida beach (s1): sky, sun, torn sea strips, kraft sand, a palm, a sign */
  function beach() {
    layer('beach', 0, 0, 1920, 1080, () => {
      g.fillStyle = paperTile('#bfe0e6', 4); g.fillRect(0, 0, 1920, 1080);
      paperCircle(1540, 190, 96, PAL.mustard, { seed: 81, depth: 0.8, boil: false });
      paperRect(420, 170, 330, 70, PAL.white, { seed: 82, depth: 0.6, torn: true, boil: false, rot: 0.02 });
      paperRect(1180, 120, 260, 56, PAL.white, { seed: 83, depth: 0.6, torn: true, boil: false, rot: -0.03 });
      paperRect(960, 560, 2100, 150, '#5f95b3', { seed: 84, torn: true, depth: 0.8, boil: false, rot: -0.004 });
      paperRect(960, 612, 2100, 110, COL.ocean, { seed: 85, torn: true, depth: 0.8, boil: false, rot: 0.006 });
      paperRect(960, 650, 2100, 60, '#9cc7cf', { seed: 86, torn: true, depth: 0.6, boil: false, rot: -0.005 });
      paperRect(960, 900, 2140, 520, '#e2c48e', { seed: 87, torn: true, depth: 1.2, boil: false, tex: 'tex_kraft', rot: 0.004 });
      // a palm: kraft trunk segments + paper fronds
      for (let i = 0; i < 9; i++) { const u = i / 8; paperRect(1330 + Math.sin(u * 1.4) * 70, 820 - u * 430, 58 - u * 16, 64, i % 2 ? '#a0703f' : '#b98150', { seed: 90 + i, depth: 0.5, boil: false, rot: 0.18 - u * 0.2 }); }
      const top = [1395, 385];
      [[-2.7, 250], [-2.2, 230], [-1.3, 210], [-0.6, 240], [-0.1, 230], [0.5, 200]].forEach(([a, L], k) => {
        const pts = []; for (let i = 0; i <= 10; i++) { const u = i / 10, w = Math.sin(u * Math.PI) * 34; pts.push([u * L, -w]); } for (let i = 10; i >= 0; i--) { const u = i / 10, w = Math.sin(u * Math.PI) * 14; pts.push([u * L, w + u * u * 30]); }
        paperPiece(pts, top[0], top[1], k % 2 ? PAL.green : '#5d9444', { rot: a, seed: 100 + k, depth: 0.9, boil: false });
      });
      paperCircle(1378, 402, 20, '#6b4a2a', { seed: 110, depth: 0.5, boil: false }); paperCircle(1410, 410, 18, '#6b4a2a', { seed: 111, depth: 0.5, boil: false });
      // the sign
      paperRect(760, 760, 22, 200, '#a0703f', { seed: 112, depth: 0.8, boil: false });
      paperRect(760, 690, 250, 90, PAL.cream, { seed: 113, depth: 1.0, boil: false, rot: -0.04, outline: true });
      g.save(); g.translate(760, 690); g.rotate(-0.04); g.font = `52px ${TYPE}`; g.fillStyle = PAL.navy; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('FLORIDA', 0, 4); g.restore();
      // gulls
      [[640, 300], [700, 270], [1040, 330]].forEach(([x, y], i) => ink([[x - 22, y - 6], [x - 8, y - 12], [x, y], [x + 8, y - 12], [x + 22, y - 6]], { w: 3.5, seed: 120 + i, step: 6, passes: 1 }));
    });
  }

  /* ======================================================================
     Staging: puppets, captions, chapter cards (after Energy's video_kit)
     ====================================================================== */
  const POS = {
    two: { leo: { x: 250, y: 1092, h: 540, dip: 0 }, quinn: { x: 1670, y: 1092, h: 580, dip: 0 } },
    inset: { leo: { x: 124, y: 1098, h: 300, dip: 64 }, quinn: { x: 1796, y: 1098, h: 318, dip: 64 } },
    off: { leo: { x: 110, y: 1520, h: 300, dip: 0 }, quinn: { x: 1810, y: 1520, h: 318, dip: 0 } },
    enterR: { leo: { x: -300, y: 1092, h: 540, dip: 0 }, quinn: { x: 2150, y: 1092, h: 580, dip: 0 } },
  };
  /* keys: [[t, modeBoth] | [t, modeLeo, modeQuinn] | [t, …, d]] */
  function stage(t, part, keys) {
    const out = {};
    ['leo', 'quinn'].forEach(who => {
      const posOf = k => { const m = who === 'leo' ? k[1] : (k[2] ?? k[1]); return POS[m][who]; };
      const v = Object.assign({}, posOf(keys[0]));
      for (let i = 1; i < keys.length; i++) {
        const k = keys[i]; if (t <= k[0]) continue;
        const e = E.inOutCubic(P(t, k[0], k[0] + (k[3] ?? 0.7))), p = posOf(k);
        ['x', 'y', 'h', 'dip'].forEach(f => { v[f] = lerp(v[f], p[f], e); });
      }
      const talk = part.lines.filter(l => l.who === who).reduce((a, l) => Math.max(a, win(t, l.start - 0.35, l.end + 0.15, 0.3)), 0);
      v.y += v.dip * (1 - talk);
      out[who] = v;
    });
    return out;
  }
  /* both puppets + the caption bubble. poses: {leo: 'cross'|'point'|null (auto), quinn: 'point'|null} */
  function cast(t, ctx, st, poses = {}, opts = {}) {
    const lt = ctx.caption && ctx.caption.who === 'leo' ? ctx.caption.text : '';
    const leoPose = poses.leo || (/[?!]/.test(lt) ? 'point' : 'cross');
    if (st.leo.y - st.leo.h < 1080) Puppet.draw('leo', st.leo.x, st.leo.y, st.leo.h, { env: ctx.speaking('leo'), facing: 1, t, pose: leoPose });
    if (st.quinn.y - st.quinn.h < 1080) Puppet.draw('quinn', st.quinn.x, st.quinn.y, st.quinn.h, { env: ctx.speaking('quinn'), facing: -1, t, pose: poses.quinn || null });
    bubble(ctx, st, opts);
  }
  /* caption bubbles: ≤ 2 rows (wider first, then paged at sentence ends, timed by the words) */
  const BS = () => (Player.captionSize ? Player.captionSize() : 40);
  const pageCache = new Map();
  function pages(l, w) {
    const size = BS(), key = l.id + '|' + w + '|' + l.tokens.length + '|' + size + '|' + l.dur.toFixed(2);
    if (pageCache.has(key)) return pageCache.get(key);
    const toks = l.tokens, n = toks.length, maxW = w - 56;
    g.save(); g.font = `${size}px ${HANDF}`;
    const sp = g.measureText(' ').width, ws = toks.map(k => g.measureText(k.w).width);
    g.restore();
    const rowsOf = (a, b) => { let r = 1, rw = 0; for (let i = a; i < b; i++) { if (rw > 0 && rw + sp + ws[i] > maxW) { r++; rw = 0; } rw += ws[i] + sp; } return r; };
    const out = []; let a = 0;
    while (a < n) {
      let b = a + 1; while (b < n && rowsOf(a, b + 1) <= 2) b++;
      if (b < n) {
        const lo = a + Math.max(1, Math.floor((b - a) * 0.4));
        let cut_ = -1;
        for (const re of [/[.!?]["')]?$/, /[,;:]$/]) { for (let j = b; j >= lo; j--) if (re.test(toks[j - 1].w)) { cut_ = j; break; } if (cut_ > 0) break; }
        if (cut_ > 0) b = cut_;
      }
      out.push([a, b]); a = b;
    }
    const res = out.map(([a, b], k) => {
      const t0 = k === 0 ? 0 : Math.max(0, toks[a].s - 0.15);
      return { t0, text: toks.slice(a, b).map(x => x.w).join(' '), rows: rowsOf(a, b), tokens: toks.slice(a, b).map(x => ({ w: x.w, s: x.s - t0, e: x.e - t0 })) };
    });
    if (pageCache.size > 300) pageCache.clear();
    pageCache.set(key, res);
    return res;
  }
  function fit(l, w0, maxW) {
    const BLH = BS() * 1.18;
    for (let w = w0; w <= maxW; w += 40) { const pg = pages(l, w); if (pg.length === 1) return { w, pages: pg, h: pg[0].rows * BLH + 40 }; }
    const pg = pages(l, w0);
    return { w: w0, pages: pg, h: Math.max(...pg.map(x => x.rows)) * BLH + 40 };
  }
  const pageAt = (pg, lt) => { let k = 0; for (let i = 1; i < pg.length; i++) if (lt >= pg[i].t0) k = i; return pg[k]; };
  /* place the caption next to its speaker: above the head (full size) or in the bottom band (inset); opts.at[id]
     overrides; opts.band = y centre of the inset band */
  function bubble(ctx, st, opts = {}) {
    const l = ctx.caption; if (!l) return;
    if (opts.at && opts.at[l.id]) { ctx.captionAt = opts.at[l.id]; return; }
    const p = st[l.who]; if (!p) return;
    if (p.y - p.h > 1040) { ctx.captionAt = 'bottom'; return; }
    const dir = l.who === 'leo' ? 1 : -1, side = dir > 0 ? 'left' : 'right';
    const tall = p.h > 420, k = BS() / 40;
    const w0 = Math.round((opts.w || (tall ? 620 : 700)) * k);
    const f = fit(l, w0, Math.round((opts.maxW || (tall ? 780 : 860)) * k));
    const w = f.w, bh = f.h;
    let cx, cy;
    if (tall) { const tipX = p.x + dir * 0.17 * p.h, tipY = p.y - 0.93 * p.h; cx = tipX + dir * (w / 2 - 10); cy = tipY - bh / 2 - 44; }
    else { cx = p.x + dir * (0.2 * p.h + w / 2 + 16); cy = Math.min(opts.band ?? 876, 968 - bh / 2); }
    cx = Math.max(w / 2 + 16, Math.min(1904 - w / 2, cx));
    cy = Math.max(bh / 2 + 16, cy);
    const cap = { x: cx, y: cy, side, w };
    if (f.pages.length > 1) { const pg = pageAt(f.pages, ctx.t - l.start); Object.assign(cap, { text: pg.text, tokens: pg.tokens, t0: pg.t0 }); }
    ctx.captionAt = cap;
  }

  /* ---------- chapter cards ---------- */
  const CARD = { hold: 1.0, tear: 0.75, lead: 2.2, wipe: 0.55 };
  function drawCard(i) {
    const seg = Game.content.script.segments[i]; if (!seg) return;
    const n = i + 1;
    g.fillStyle = paperTile(PAL.kraft, 23, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
    paperRect(960, 548, 1790, 990, COL.survey, { seed: 900 + n, depth: 1.6, boil: false });
    GroundLook.contours(960, 548, 1760, 960, 40 + n, 0.8);
    tape(170, 90, 190, -0.55, undefined, 910 + n); tape(1750, 90, 190, 0.55, undefined, 920 + n);
    hand('chapter', 960, 250, 58, -1e6, { dur: 0.01, color: PAL.navy, seed: 930 + n, font: TYPE });
    ransom(String(n), 960, 420, 220, -1e6, { seed: 700 + n });
    hand(seg.chapter, 960, 650, 128, -1e6, { dur: 0.01, color: PAL.navy, seed: 940 + n, rot: -0.02 });
    g.save(); g.font = `128px ${HANDF}`; const w = g.measureText(seg.chapter).width; g.restore();
    ink([[960 - w / 2 - 10, 730], [960, 722], [960 + w / 2 + 16, 734]], { w: 8, color: PAL.red, seed: 950 + n, passes: 2 });
    // a torn strata doodle in the corner, cracked (the lesson's running gag: the ground is never still)
    [[COL.limestone, 0], [COL.sandstone, 1], [COL.shale, 2], [COL.mantle, 3]].forEach(([c, k]) => paperRect(1560, 850 + k * 34, 320, 40, c, { seed: 960 + n * 5 + k, torn: true, depth: 0.5, boil: false, rot: -0.02 + k * 0.01 }));
    ink([[1520, 826], [1545, 870], [1530, 905], [1560, 950], [1548, 990]], { w: 4, seed: 970 + n, step: 8, passes: 1 });
    hand('~ ' + String((Game.lesson && Game.lesson.title) || '').toLowerCase() + ' ~', 520, 930, 40, -1e6, { dur: 0.01, color: 'rgba(37,60,90,0.6)', seed: 980 + n });
  }
  function tornBand(edge, seed, dirIn) {
    const r = rng(seed + 1), inner = edge.map(q => [q[0] + dirIn * (10 + r() * 14), q[1]]);
    const band = [...edge, ...inner.reverse()];
    g.save(); pathPts(band); g.fillStyle = paperTile(PAL.white, 2); g.fill();
    g.globalAlpha = 0.35; g.strokeStyle = '#d9ccb2'; g.lineWidth = 1.2; pathPts(edge, false); g.stroke(); g.restore();
  }
  function cardOut(t, t0, dur, i, seed = 17) {
    const p = E.inOutCubic(P(t, t0, t0 + dur)); if (p >= 1) return;
    const edge = tornEdgePts(lerp(1920 + 220, -260, p), seed), poly = [[-100, -100], ...edge, [-100, 1180]];
    g.save(); shadowFill(poly, 2.2, PAL.paper); g.restore();
    g.save(); pathPts(poly); g.clip(); drawCard(i); g.restore();
    tornBand(edge, seed, -1);
  }
  function cardIn(t, t0, dur, i, seed = 29) {
    const p = E.inOutCubic(P(t, t0, t0 + dur)); if (p <= 0) return;
    const edge = tornEdgePts(lerp(1920 + 220, -260, p), seed), poly = [[2100, -100], ...edge, [2100, 1180]];
    g.save(); shadowFill(poly, 2.2, PAL.paper); g.restore();
    g.save(); pathPts(poly); g.clip(); drawCard(i); g.restore();
    tornBand(edge, seed, 1);
  }
  function chapter(t, part) {
    const i = segIndex(part.seg);
    if (part.kind === 'lines' && t < CARD.hold + CARD.tear) { if (t < CARD.hold) { drawCard(i); return; } cardOut(t, CARD.hold, CARD.tear, i); }
    if (part.kind === lastKind(part.seg) && i < Game.content.script.segments.length - 1) cardIn(t, part.dur - CARD.wipe - 0.05, CARD.wipe, i + 1);
  }
  function chapterSfx(part) {
    const out = [], i = segIndex(part.seg);
    if (part.kind === 'lines') out.push({ name: 'tear', t: CARD.hold, gain: 0.55 });
    if (part.kind === lastKind(part.seg) && i < Game.content.script.segments.length - 1) out.push({ name: 'paper', t: part.dur - CARD.wipe - 0.05, gain: 0.5 });
    return out;
  }
  /* sub-scene changes inside a part: a torn page (pure in t) */
  function scenes(t, list) {
    // list: [[tStart, draw], …] in order; the change to list[i] tears over [tStart, tStart + 0.75]
    let i = 0; for (let k = 1; k < list.length; k++) if (t >= list[k][0]) i = k;
    const t0 = list[i][0];
    if (i > 0 && t < t0 + 0.75) tornTransition(u => list[i - 1][1](u), u => list[i][1](u), t, t0, 0.75, 11 + i);
    else list[i][1](t);
    return i;
  }

  /* ---------- paper bits ---------- */
  const label = (str, x, y, size, t0, o = {}) => hand(str, x, y, size, t0, Object.assign({ color: PAL.navy, seed: 5 }, o));
  function strike(x0, y0, x1, y1, prog, seed = 1, color = PAL.red, w = 9) { ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w, color, seed, prog, passes: 2, step: 10 }); }
  function check(x, y, s, prog, seed = 1) { ink([[x - 34 * s, y - 2 * s], [x - 8 * s, y + 26 * s], [x + 40 * s, y - 38 * s]], { w: 11 * s, color: PAL.teal, seed, prog, passes: 2, step: 8 }); }
  function cross(x, y, s, prog, seed = 1) {
    ink([[x - 30 * s, y - 30 * s], [x + 30 * s, y + 30 * s]], { w: 11 * s, color: PAL.red, seed, prog: clamp(prog * 2), passes: 2, step: 8 });
    ink([[x + 30 * s, y - 30 * s], [x - 30 * s, y + 30 * s]], { w: 11 * s, color: PAL.red, seed: seed + 3, prog: clamp(prog * 2 - 1), passes: 2, step: 8 });
  }
  /* a small paper tag (label on a card) */
  function tag(str, x, y, size, s = 1, o = {}) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.04); g.scale(s, s);
    g.font = `${size}px ${o.font || HANDF}`;
    const w = g.measureText(str).width + size * 0.9, h = size * 1.35;
    paperRect(0, 0, w, h, o.color || PAL.cream, { seed: o.seed || 41, depth: 1.0, outline: true });
    g.fillStyle = o.ink || PAL.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, 0, size * 0.05);
    g.restore();
  }
  /* an index card with hand-written rows [{s, t0, color, size}] (x, y = centre) */
  function indexCard(x, y, w, h, rows, { s = 1, rot = -0.015, seed = 300, alpha = 1 } = {}) {
    if (s <= 0 || alpha <= 0) return;
    g.save(); g.globalAlpha *= alpha; g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, w, h, PAL.white, { seed, depth: 1.3 });
    g.save(); g.strokeStyle = 'rgba(90,150,190,0.3)'; g.lineWidth = 2; g.beginPath();
    for (let yy = -h / 2 + 70; yy < h / 2 - 10; yy += 52) { g.moveTo(-w / 2 + 16, yy); g.lineTo(w / 2 - 16, yy); }
    g.stroke(); g.strokeStyle = 'rgba(224,83,61,0.4)'; g.beginPath(); g.moveTo(-w / 2 + 16, -h / 2 + 52); g.lineTo(w / 2 - 16, -h / 2 + 52); g.stroke(); g.restore();
    rows.forEach((r, i) => { if (r) hand(r.s, r.x ?? (-w / 2 + 40), -h / 2 + 32 + i * 52 + (r.dy || 0), r.size || 40, r.t0, { align: r.align || 'left', color: r.color || PAL.ink, seed: seed + 10 + i, dur: r.dur }); });
    g.restore();
    tape(x, y - h / 2 * s + 2, 120 * s, 0.04, undefined, seed + 1);
  }
  /* the sticky note with the viewer's answer, then ✓ or ✗ */
  function answerNote(ctx, part, x, y, t0, tMark, o = {}) {
    const q = ctx.question; if (!q) return;
    const ans = ctx.answer != null ? ctx.answer : part.answer != null ? part.answer : q.correct;
    const s = pop(T, t0, 0.35); if (s <= 0) return;
    const w = o.w || 360, lines = UI._wrap(q.options[ans], w - 60, `34px ${HANDF}`), h = 92 + lines.length * 38;
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.04); g.scale(s, s);
    paperRect(0, 0, w, h, PAL.gold, { seed: 520, depth: 1.3 });
    g.restore();
    tape(x, y - h / 2 + 4, 110, 0.05, undefined, 521);
    hand('you said:', x - w / 2 + 28, y - h / 2 + 40, 30, t0 + 0.1, { align: 'left', color: 'rgba(43,36,32,0.65)', seed: 522, rot: o.rot ?? -0.04 });
    lines.forEach((l, i) => hand(l, x, y - h / 2 + 80 + i * 38, 34, t0 + 0.25 + i * 0.2, { color: PAL.ink, seed: 523 + i, rot: o.rot ?? -0.04 }));
    const right = ans === q.correct, mp = P(T, tMark, tMark + 0.35);
    if (right) check(x + w / 2 - 20, y - h / 2 + 10, 1.1, mp, 530); else cross(x + w / 2 - 24, y - h / 2 + 14, 0.9, mp, 530);
  }
  /* a school ruler (30 cm) centred at (x, y), length L px, rotated */
  function ruler(x, y, L, rot = 0, { s = 1, seed = 140, mark = null, alpha = 1 } = {}) {
    if (s <= 0 || alpha <= 0) return;
    const h = Math.max(14, L * 0.085);
    g.save(); g.globalAlpha *= alpha; g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, L, h, '#f2d27a', { seed, depth: 1.0, outline: true });
    g.strokeStyle = 'rgba(43,36,32,0.8)'; g.lineWidth = Math.max(1, L / 520); g.beginPath();
    for (let cm = 0; cm <= RULER_CM; cm++) { const px = -L / 2 + L * 0.03 + (L * 0.94) * cm / RULER_CM, tl = cm % 10 === 0 ? h * 0.55 : cm % 5 === 0 ? h * 0.4 : h * 0.25; g.moveTo(px, -h / 2); g.lineTo(px, -h / 2 + tl); }
    g.stroke();
    if (L > 300) { g.fillStyle = PAL.ink; g.font = `${Math.round(h * 0.34)}px ${TYPE}`; g.textAlign = 'center'; g.textBaseline = 'middle';
      for (let cm = 0; cm <= RULER_CM; cm += 5) g.fillText(String(cm), -L / 2 + L * 0.03 + (L * 0.94) * cm / RULER_CM, h * 0.18); }
    if (mark != null) { const px = -L / 2 + L * 0.03 + (L * 0.94) * mark / RULER_CM; g.fillStyle = 'rgba(224,83,61,0.35)'; g.fillRect(-L / 2 + L * 0.03, -h / 2 + 2, px + L / 2 - L * 0.03, h - 4); ink([[px, -h / 2 - 12], [px, h / 2 + 12]], { w: 5, color: PAL.red, seed: seed + 3, step: 8 }); }
    g.restore();
  }
  const rulerX = (x, L, cm) => x - L / 2 + L * 0.03 + (L * 0.94) * cm / RULER_CM;

  /* torn circle outline (cached) for the cross-section's layers */
  const _torn = new Map();
  function tornCircle(r, seed) {
    const k = r + ',' + seed; if (_torn.has(k)) return _torn.get(k);
    const rr = rng(seed), pts = [], n = Math.max(60, Math.round(r * TAU / 9)); let walk = 0;
    for (let i = 0; i < n; i++) { walk = walk * 0.6 + (rr() - 0.5) * 5; const a = i / n * TAU, R = r + walk + (rr() - 0.5) * 2.5; pts.push([Math.cos(a) * R, Math.sin(a) * R]); }
    pts.k = 'tc' + k; _torn.set(k, pts); return pts;
  }

  /* ======================================================================
     s1 — "Florida doesn't move": the beach → the globe → the ruler across the Atlantic
     ====================================================================== */
  const G1 = { x: 960, y: 450, R: 360 };
  const RULER_ENDS = [[-74, 35], [-14, 23]];          // lon/lat of the ruler's ends: US east coast → NW Africa
  function s1Globe(t, c, { pull = 1, apart = 1, rulerIn = 1, boat = 0 } = {}) {
    const { x, y, R } = G1;
    globe(x, y, R, { apart: 10 * apart, ridge: apart });
    // paper arrows: the two sides drift apart
    if (apart > 0) {
      const [lx, ly] = globePt(-50, 30, x, y, R), [rx, ry] = globePt(-24, 26, x, y, R), s = pop(T, c.W('s1_05', 'apart'), 0.4) * (1 - P(T, c.W('s1_07', 'two'), c.W('s1_07', 'two') + 0.4));
      paperArrow(lx - 6, ly, 90, 26, PAL.red, { rot: Math.PI, s, seed: 151 });
      paperArrow(rx + 6, ry, 90, 26, PAL.red, { rot: 0, s, seed: 152 });
    }
    if (boat > 0) {
      const [bx, by] = globePt(-40, 22, x, y, R), bob = Math.sin(T * 2.2) * 5;
      g.save(); g.translate(bx, by + bob); g.rotate(Math.sin(T * 1.7) * 0.08); g.scale(boat, boat);
      paperPiece([[-46, 0], [46, 0], [30, 22], [-30, 22]], 0, 0, PAL.white, { seed: 153, depth: 0.8 });
      paperPiece([[-4, -2], [-4, -64], [34, -8]], 0, 0, PAL.cream, { seed: 154, depth: 0.6 });
      g.restore();
    }
    if (rulerIn > 0) {
      const [ax, ay] = globePt(...RULER_ENDS[0], x, y, R), [bx, by] = globePt(...RULER_ENDS[1], x, y, R);
      const L = Math.hypot(bx - ax, by - ay), rot = Math.atan2(by - ay, bx - ax);
      const e = E.outCubic(rulerIn), cx = lerp(1700, (ax + bx) / 2, e), cy = lerp(150, (ay + by) / 2, e);
      ruler(cx, cy, L, lerp(-0.7, rot, e), { seed: 141 });
      if (rulerIn >= 1) { tape(ax + 20, ay, 70, rot + 1.3, undefined, 142); tape(bx - 20, by, 70, rot + 1.3, undefined, 143); }
    }
  }
  function s1Lines(t, part, ctx, c) {
    const t2 = c.L('s1_02'), t5 = c.L('s1_05');
    const pull = E.inOutCubic(P(t, t5 + 0.2, t5 + 2.4));
    const tNail = c.W('s1_02', 'fingernails'), tToe = c.W('s1_04', 'toenail'), tAtl = c.W('s1_04', 'Atlantic');
    // the globe behind (space), then the beach shrinking into Florida
    if (pull > 0) {
      spaceBackdrop();
      const zg = lerp(3.4, 1, pull), [fx, fy] = globePt(-81.5, 28, G1.x, G1.y, G1.R);
      g.save(); g.translate(fx, fy); g.scale(zg, zg); g.translate(-fx, -fy);
      s1Globe(t, c, { apart: E.inOutSine(P(t, c.W('s1_05', 'apart'), c.W('s1_05', 'apart') + 2.5)), rulerIn: P(t, c.W('s1_07', 'two'), c.W('s1_07', 'two') + 0.9), boat: pop(t, c.W('s1_06', 'boat'), 0.4) * unpop(t, c.W('s1_07', 'two')) });
      g.restore();
      if (t > c.W('s1_07', 'wider')) tag('+' + fmtNum(Plates.ATLANTIC) + ' cm a year', 1470, 300, 46, pop(t, c.W('s1_07', 'wider'), 0.35), { color: PAL.mustard, seed: 160, rot: 0.03 });
      if (t > c.W('s1_05', 'plate')) label('you are here', G1.x - 245, G1.y - 175, 40, c.W('s1_05', 'plate') + 0.2, { color: PAL.cream, outline: OUT, seed: 161, rot: -0.05 });
      if (t > c.W('s1_05', 'plate') + 0.2) { const [fx2, fy2] = globePt(-81.5, 28, G1.x, G1.y, G1.R); inkCircle(fx2, fy2, 26, { w: 5, color: PAL.cream, seed: 162, prog: P(t, c.W('s1_05', 'plate') + 0.2, c.W('s1_05', 'plate') + 0.7) }); }
    }
    if (pull < 1) {
      const [fx, fy] = globePt(-81.5, 28, G1.x, G1.y, G1.R), zb = lerp(1, 0.035, E.inCubic(pull));
      g.save(); g.globalAlpha *= 1 - P(pull, 0.55, 0.9);
      g.translate(lerp(960, fx, pull), lerp(540, fy, pull)); g.scale(zb, zb); g.translate(-960, -540);
      if (pull > 0) paperRect(960, 540, 1920 + 60, 1080 + 60, PAL.white, { seed: 159, depth: 2 });
      beach();
      // "you're moving right now": a tiny drift arrow at Leo's feet
      if (t > c.W('s1_02', 'moving')) { const s = pop(t, c.W('s1_02', 'moving'), 0.35); paperArrow(390, 1010, 130, 24, PAL.red, { s, seed: 163 }); label('you (moving)', 620, 1004, 38, c.W('s1_02', 'moving') + 0.2, { seed: 164, color: PAL.ink }); }
      // the nails card: fingernails / toenails / the Atlantic, all from Plates
      const a = unpop(t, t5 - 0.3, 0.4);
      if (a > 0 && t > tNail) indexCard(960, 250, 760, 250, [
        { s: 'fingernails grow about ' + fmtNum(Plates.FINGERNAIL_CM_YR) + ' cm a year', t0: tNail + 0.2, size: 40 },
        { s: 'toenails: about ' + fmtNum(Plates.TOENAIL_CM_YR) + ' cm a year', t0: tToe, size: 40 },
        { s: 'the Atlantic: about ' + fmtNum(Plates.ATLANTIC) + ' cm a year', t0: tAtl, size: 40, color: PAL.red },
      ], { s: pop(t, tNail, 0.35) * a, seed: 170, rot: 0.012 });
      g.restore();
    }
  }
  /* the reply's beats: the working is written on "thirteen", the result lands on the number (or "ruler") */
  function s1Cues(part, c) {
    if (part.kind === 'after') return { tWork: -1e6, tRes: -1e6 };
    const id = replyId(part);
    return { tWork: c.W(id, 'Thirteen'), tRes: id === 'q1_wrong0' ? c.W(id, 'ruler') - 0.3 : c.W(id, 'twenty') };
  }
  function s1Reply(t, part, ctx, c) {
    surveyBackdrop(3);
    const q = part.seg.question, years = q.given.years, rate = Plates.ATLANTIC;
    if (q.given.rate !== rate) console.warn('[video] q1 rate differs from Plates.ATLANTIC', q.given.rate, rate);
    const cm = Plates.cmIn(years, rate), rows = [];
    const id = replyId(part), after = part.kind === 'after';
    const { tWork, tRes } = s1Cues(part, c);
    // the wrong-2 detour: "three km would be ten thousand rulers" (the km figure is the option's own number)
    if (!after && id === 'q1_wrong2') {
      const km = +(q.options[2].match(/[\d.]+/) || [0])[0], cmKm = km * 100000, n = cmKm / RULER_CM, t0 = c.W(id, 'Three');
      indexCard(1000, 120, 860, 130, [{ s: fmtNum(km) + ' km = ' + fmtInt(cmKm) + ' cm = ' + fmtInt(n) + ' rulers', t0: t0 + 0.1, size: 44 }], { seed: 180, s: pop(t, t0 - 0.2, 0.35) });
      if (t > c.W(id, 'slower')) strike(600, 118, 1400, 100, P(t, c.W(id, 'slower'), c.W(id, 'slower') + 0.4), 181);
    }
    if (!after && id === 'q1_wrong0') {
      const t0 = c.W(id, 'moving');
      if (t > t0) UI.stamp('IT MOVES', 800, 120, { s: 0.7 * pop(t, t0, 0.3), rot: -0.06 });
      if (t > c.W(id, 'GPS')) tag('GPS stations measure it', 1330, 125, 42, pop(t, c.W(id, 'GPS'), 0.35), { seed: 182, color: PAL.sky });
    }
    const show = P(t, tWork - 0.4, tWork);
    if (show > 0) {
      indexCard(960, 350, 1000, 250, [
        { s: years + ' years × ' + fmtNum(rate) + ' cm a year', t0: tWork, size: 52 },
        { s: '= ' + fmtNum(cm) + ' cm', t0: tRes, size: 60, color: PAL.red, dy: 12 },
        { s: 'about one school ruler', t0: tRes + 0.8, size: 44, dy: 14 },
      ], { seed: 185, s: pop(t, tWork - 0.4, 0.35) });
      const L = 1000, rx = 960, ry = 640, rs = pop(t, tRes - 0.2, 0.35);
      ruler(rx, ry, L, 0.01, { s: rs, seed: 186, mark: t > tRes ? Math.min(cm, RULER_CM) * E.outCubic(P(t, tRes, tRes + 0.8)) : null });
      if (t > tRes + 0.8) label(fmtNum(cm) + ' cm', rulerX(rx, L, cm), ry + 110, 44, tRes + 0.8, { color: PAL.red, seed: 187 });
    }
    // after: "wait until you see what it adds up to": ghost rulers heading off to the right
    if (after) {
      const t9 = c.W('s1_09', 'adds');
      for (let k = 1; k <= 5; k++) { const a = P(t, t9 + k * 0.18, t9 + k * 0.18 + 0.3) * (1 - k * 0.15); if (a > 0) ruler(960 + k * 1010, 640, 1000, 0.01, { alpha: a * 0.6, seed: 186 }); }
    } else answerNote(ctx, part, 250, 170, 0.35, 0.9, { w: 340 });
  }
  S.s1 = {
    get timing() { return { lead: { lines: CARD.lead }, hold: { lines: 1.2, reply: 1.2, after: 1.0 }, gaps: { s1_05: 0.8, s1_07: 0.7 } }; },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      if (part.kind === 'lines') {
        out.push({ name: 'pop', t: c.W('s1_02', 'fingernails'), gain: 0.35 }, { name: 'scribble', t: c.W('s1_04', 'toenail'), gain: 0.3 }, { name: 'scribble', t: c.W('s1_04', 'Atlantic'), gain: 0.3 });
        out.push({ name: 'hammer', t: c.L('s1_02') - 0.4, gain: 0.4 });
        out.push({ name: 'whoosh', t: c.L('s1_05') + 0.2, gain: 0.5 });
        out.push({ name: 'grind', t: c.W('s1_05', 'apart'), gain: 0.25, loop: true, dur: 2.5 });
        out.push({ name: 'whoosh', t: c.W('s1_07', 'two'), gain: 0.35, rate: 1.3 }, { name: 'thunk', t: c.W('s1_07', 'two') + 0.9, gain: 0.4 });
      } else {
        out.push({ name: 'scribble', t: 0.6, gain: 0.3 });
        const id = replyId(part);
        if (part.kind === 'reply') { const k = s1Cues(part, c); out.push({ name: 'scribble', t: k.tWork, gain: 0.3 }, { name: 'ding', t: k.tRes + 0.4, gain: 0.4 }); }
        if (id === 'q1_wrong0') out.push({ name: 'stamp', t: c.W(id, 'moving'), gain: 0.5 });
      }
      return out;
    },
    draw(t, part, ctx) {
      const c = cues(ctx);
      if (part.kind === 'lines') {
        s1Lines(t, part, ctx, c);
        const t2 = c.L('s1_02'), t5 = c.L('s1_05');
        const st = stage(t, part, [[0, 'two', 'enterR'], [t2 - 0.7, 'two', 'two', 0.8], [t5 + 0.1, 'inset', 'inset', 0.9]]);
        cast(t, ctx, st, { quinn: t > t5 && t < c.E('s1_05') ? 'point' : null });
      } else {
        s1Reply(t, part, ctx, c);
        const st = stage(t, part, [[0, 'inset']]);
        cast(t, ctx, st);
      }
      chapter(t, part);
    },
  };

  /* ======================================================================
     s2 — "Quakes draw the map": every M5+ quake of 2025, month by month; the dots link into the plate edges
     ====================================================================== */
  const MAPC = { x: 960, y: 425, w: 1700, h: 740 };
  const WV = worldView(MAPC.x, MAPC.y, MAPC.w, MAPC.h);
  const MIAMI = [-80.19, 25.76];             // the city the lesson measures from (Plates.FLORIDA_EDGE_KM.miami)
  const MIDDLES = [[-52, -9], [-102, 56], [133, -25], [100, 62]];   // continent middles circled in q2_wrong2 (tools/qa_video.js checks they're near-empty)
  function worldCard() {
    layer('worldmap', MAPC.x - MAPC.w / 2 - 20, MAPC.y - MAPC.h / 2 - 20, MAPC.w + 40, MAPC.h + 40, () => {
      GroundLook.survey(MAPC.x, MAPC.y, MAPC.w, MAPC.h, { seed: 21, depth: 1.4, grid: false, contour: 0.35 });
      geoBase(WV);
    });
  }
  /* quakes of months < m, baked (re-baked when m changes) */
  function quakeLayer(m) {
    const G = geo(); if (!G) return;
    layer('quakes', MAPC.x - MAPC.w / 2, MAPC.y - MAPC.h / 2, MAPC.w, MAPC.h, () => {
      for (let k = 0; k < m; k++) quakeDots(WV, G.byMonth[k]);
    }, { ver: m });
  }
  /* the 12-month schedule on the part clock */
  function s2Sched(c) {
    const t0 = c.W('s2_01', 'every') + 0.2, t1 = Math.max(t0 + 6, c.E('s2_02') - 0.3);
    return { t0, t1, dm: (t1 - t0) / 12 };
  }
  function s2Map(t, c, { zoom = 0, plates = 0, dim = 1, sched = null } = {}) {
    const G = geo(); if (!G) return;
    const k = sched ? clamp(Math.floor((t - sched.t0) / sched.dm), 0, 12) : 12;
    if (zoom <= 0.001) {
      worldCard();
      g.save(); g.globalAlpha *= dim; quakeLayer(k); g.restore();
      if (k < 12 && sched) {
        const m = k, ms = sched.t0 + m * sched.dm, len = G.starts[m + 1] - G.starts[m];
        quakeDots(WV, G.byMonth[m], { pop: r => pop(t, ms + (r[4] - G.starts[m] - 1) / len * sched.dm, 0.3) });
      }
      if (plates > 0) plateReveal(WV, plates);
      return;
    }
    // zoomed: the map card as vectors (crisp), camera on the Caribbean
    const z = lerp(1, 3.3, zoom), fx = lerp(0, -81, zoom), fy = lerp(12, 24, zoom);
    const v = { x: MAPC.x, y: MAPC.y, w: MAPC.w, h: MAPC.h, s: WV.s * z, lon0: fx, lat0: fy };
    worldCard();                             // the paper (its own map shows through only at the edges)
    g.save(); g.beginPath(); g.rect(MAPC.x - MAPC.w / 2 + 16, MAPC.y - MAPC.h / 2 + 16, MAPC.w - 32, MAPC.h - 32); g.clip();
    g.fillStyle = paperTile(COL.survey, 21); g.globalAlpha = P(zoom, 0, 0.25); g.fillRect(0, 0, 1920, 1080); g.globalAlpha = 1;
    g.restore();
    geoBase(v, { coastW: 2 });
    quakeDots(v, G.q, { k: Math.sqrt(z), alpha: dim });
    plateReveal(v, 1);
  }
  const s2Zoom = (t, c) => E.inOutCubic(P(t, c.L('s2_04') - 0.3, c.L('s2_04') + 1.2));
  function s2Credits(t, c, plates) {
    const G = geo(); if (!G) return;
    const x0 = MAPC.x - MAPC.w / 2 + 44, y0 = MAPC.y + MAPC.h / 2 - 34;
    const src = String(G.Q.source || '').split(',')[0];
    // each credit on a small paper strip, so it never sits on the data
    const strip = (str, x, y, size, align, seed, alpha = 1) => {
      if (alpha <= 0) return;
      g.save(); g.font = `${size}px ${HANDF}`; const w = g.measureText(str).width + 28; g.restore();
      const cx = align === 'left' ? x - 14 + w / 2 : x + 14 - w / 2;
      g.save(); g.globalAlpha *= alpha; paperRect(cx, y, w, size * 1.3, PAL.cream, { seed, depth: 0.5, boil: false }); g.restore();
      hand(str, x, y, size, -1e6, { dur: 0.01, align, color: 'rgba(43,36,32,0.8)', seed: seed + 1, alpha });
    };
    strip(src + ', ' + G.year + ': ' + fmtInt(G.q.length) + ' quakes of magnitude ' + G.Q.minMag + ' or more', x0, y0, 28, 'left', 201);
    strip(G.B.credit, MAPC.x + MAPC.w / 2 - 44, y0, 26, 'right', 204, plates);
    hand('Made with ' + String(G.C.source || '').split(' ').slice(0, 2).join(' '), MAPC.x + MAPC.w / 2 - 44, MAPC.y - MAPC.h / 2 + 40, 24, -1e6, { dur: 0.01, align: 'right', color: 'rgba(43,36,32,0.55)', seed: 203 });
  }
  function monthTag(t, sched) {
    const G = geo(); if (!G) return;
    const k = clamp(Math.floor((t - sched.t0) / sched.dm), 0, 11), done = t >= sched.t1;
    const n = done ? G.q.length : G.byMonth.slice(0, k).reduce((a, m) => a + m.length, 0) + G.byMonth[k].filter(r => t >= sched.t0 + k * sched.dm + (r[4] - G.starts[k] - 1) / (G.starts[k + 1] - G.starts[k]) * sched.dm).length;
    const s = pop(t, sched.t0 - 0.3, 0.35);
    if (s <= 0) return;
    const x = 250, y = 150;
    g.save(); g.translate(x, y); g.rotate(-0.03); g.scale(s, s);
    paperRect(0, 0, 300, 124, PAL.white, { seed: 205, depth: 1.2, outline: true });
    g.fillStyle = PAL.ink; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.font = `46px ${TYPE}`; g.fillText((done ? 'DEC' : MONTHS[k]) + ' ' + G.year, 0, -22);
    g.font = `38px ${HANDF}`; g.fillStyle = PAL.red; g.fillText(fmtInt(n) + ' quakes', 0, 30);
    g.restore();
    tape(x, y - 60, 110, 0.05, undefined, 206);
  }
  function s2Lines(t, part, ctx, c) {
    surveyBackdrop(3);
    const sched = s2Sched(c);
    s2Map(t, c, { sched });
    s2Credits(t, c, 0);
    monthTag(t, sched);
  }
  function s2Reply(t, part, ctx, c) {
    surveyBackdrop(3);
    const id = replyId(part), after = part.kind === 'after';
    const tLines = after ? -1e6 : id === 'q2_right' ? c.W(id, 'Lines') : id === 'q2_wrong0' ? c.W(id, 'long') : c.W(id, 'trace');
    const pl = E.inOutSine(P(t, tLines, tLines + 2.4));
    const zoom = after ? s2Zoom(t, c) : 0;
    const dim = after ? lerp(1, 0.55, P(t, c.L('s2_03'), c.L('s2_03') + 0.8)) : 1;
    s2Map(t, c, { zoom, plates: pl, dim });
    s2Credits(t, c, pl);
    const G = geo();
    if (!after && id === 'q2_wrong0' && G) {
      // what "sprinkled like salt" would look like: the same number of dots, uniformly random (made up, labelled)
      const t0 = c.W(id, 'sprinkled'), a = pop(t, t0, 0.35) * unpop(t, tLines + 1.2, 0.4);
      if (a > 0) {
        const cx = 1500, cy = 250, w = 420, h = 220;
        g.save(); g.translate(cx, cy); g.rotate(0.03); g.scale(a, a);
        layer('saltcard', -w / 2 - 10, -h / 2 - 10, w + 20, h + 20, () => {
          paperRect(0, 0, w, h, PAL.white, { seed: 210, depth: 1.3, outline: true });
          const r = rng(2025); g.fillStyle = QUAKE; g.beginPath();
          for (let i = 0; i < G.q.length; i++) { const x = -w / 2 + 14 + r() * (w - 28), y = Math.asin(r() * 2 - 1) / (Math.PI / 2) * (h / 2 - 40) + 10; g.moveTo(x + 1.4, y); g.arc(x, y, 1.4, 0, TAU); }
          g.fill();
          g.font = `30px ${HANDF}`; g.fillStyle = PAL.navy; g.textAlign = 'center'; g.fillText('if they were random (made up)', 0, -h / 2 + 26);
        });
        g.restore();
      }
    }
    if (!after && id === 'q2_wrong2') {
      const t0 = c.W(id, 'middles'), a = unpop(t, tLines + 1.5, 0.4);
      MIDDLES.forEach(([lon, lat], i) => { if (a > 0) { g.save(); g.globalAlpha *= a; inkCircle(vX(WV, lon), vY(WV, lat), 62, { w: 5, color: PAL.navy, seed: 220 + i, prog: P(t, t0 + i * 0.15, t0 + i * 0.15 + 0.5), sy: 0.8 }); g.restore(); } });
      if (a > 0 && t > t0) label('almost empty', vX(WV, -52), vY(WV, -9) + 78, 38, t0 + 0.4, { seed: 225, alpha: a });
    }
    if (!after && t > c.W(id, 'edges') - 0.3 && id !== 'q2_wrong2') tag('the edges of the plates', 1230, 108, 44, pop(t, c.W(id, 'edges'), 0.35) * (id === 'q2_wrong0' ? unpop(t, 1e6) : 1), { color: PAL.mustard, seed: 226 });
    if (!after && id === 'q2_wrong2' && t > c.W(id, 'nine')) tag('about ' + Math.round(Plates.PLATE_EDGE_SHARE * 10) + ' in 10 at plate edges', 1230, 108, 44, pop(t, c.W(id, 'nine'), 0.35), { color: PAL.mustard, seed: 227 });
    if (!after) answerNote(ctx, part, 520, 880, 0.35, 0.9, { w: 340, rot: -0.03 });
    if (after) {
      // Florida, circled; a pencil line to the nearest plate edge (the label is Plates'; the line is measured on the data)
      const v = { x: MAPC.x, y: MAPC.y, s: WV.s * lerp(1, 3.3, zoom), lon0: lerp(0, -81, zoom), lat0: lerp(12, 24, zoom) };
      const tF = c.W('s2_04', 'Florida'), tH = c.W('s2_04', 'Hundreds');
      if (t > tF && zoom > 0.9) inkCircle(vX(v, -81.7), vY(v, 28.2), 118, { w: 7, color: PAL.navy, seed: 230, prog: P(t, tF, tF + 0.6), sy: 1.25 });
      const ne = GroundVideo.edge();
      if (ne && t > tH && zoom > 0.9) {
        const ax = vX(v, MIAMI[0]), ay = vY(v, MIAMI[1]), bx = vX(v, ne.lon), by = vY(v, ne.lat);
        g.save(); g.setLineDash([14, 10]); g.strokeStyle = PAL.navy; g.lineWidth = 5; const p = P(t, tH, tH + 0.8);
        g.beginPath(); g.moveTo(ax, ay); g.lineTo(lerp(ax, bx, p), lerp(ay, by, p)); g.stroke(); g.restore();
        paperCircle(ax, ay, 9, PAL.navy, { seed: 231, depth: 0.4 });
        if (p >= 1) paperCircle(bx, by, 9, PLATE_INK, { seed: 232, depth: 0.4 });
        tag('Miami: about ' + fmtInt(Plates.FLORIDA_EDGE_KM.miami) + ' km to the nearest plate edge', 1380, 150, 42, pop(t, tH + 0.6, 0.35), { seed: 233, color: PAL.white });
      }
      const tN = c.W('s2_05', 'never');
      if (t > tN) UI.stamp('RARE. NOT NEVER.', 1460, 400, { s: 0.62 * pop(t, tN, 0.3), rot: -0.07 });
    }
  }
  S.s2 = {
    get timing() { return { lead: { lines: CARD.lead }, hold: { lines: 1.4, reply: 1.4, after: 1.2 }, gaps: { s2_02: 1.2, s2_04: 0.9 } }; },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      if (part.kind === 'lines') {
        const sc = s2Sched(c);
        for (let m = 0; m < 12; m++) out.push({ name: 'pop', t: sc.t0 + m * sc.dm, gain: 0.22, rate: 0.9 + m * 0.03 });
        out.push({ name: 'quake', t: sc.t0, gain: 0.3, dur: sc.t1 - sc.t0 });
      } else if (part.kind === 'reply') {
        const id = replyId(part), tl = id === 'q2_right' ? c.W(id, 'Lines') : id === 'q2_wrong0' ? c.W(id, 'long') : c.W(id, 'trace');
        out.push({ name: 'scribble', t: tl, gain: 0.45 }, { name: 'scribble', t: tl + 1.1, gain: 0.35 });
      } else {
        out.push({ name: 'whoosh', t: c.L('s2_04') - 0.3, gain: 0.35 }, { name: 'scribble', t: c.W('s2_04', 'Florida'), gain: 0.35 }, { name: 'stamp', t: c.W('s2_05', 'never'), gain: 0.5 });
      }
      return out;
    },
    draw(t, part, ctx) {
      const c = cues(ctx);
      if (part.kind === 'lines') s2Lines(t, part, ctx, c); else s2Reply(t, part, ctx, c);
      const st = stage(t, part, [[0, 'inset']]);
      cast(t, ctx, st, { leo: part.kind === 'after' && t > c.L('s2_04') - 0.2 && t < c.E('s2_04') + 0.2 ? 'point' : null, quinn: part.kind === 'lines' && t < c.E('s2_01') ? 'point' : null });
      chapter(t, part);
    },
  };

  /* ======================================================================
     s3 — "What's under us": the Earth cut open (torn-paper layers), then the mantle churning
     ====================================================================== */
  const X3 = { x: 960, y: 470, R: 330 };
  const RAD = { crust: 1, mantle: 0.94, outer: CMB_KM / EARTH_KM, inner: ICB_KM / EARTH_KM };
  function xsection(x, y, R) {
    const pad = 16;
    g.save(); g.translate(x, y);
    layer('xsec' + R, -R - pad, -R - pad, 2 * R + 2 * pad, 2 * R + 2 * pad + 10, () => {
      paperPiece(tornCircle(R, 301), 0, 0, COL.crust, { seed: 301, depth: 1.6, boil: false });
      paperPiece(tornCircle(R * RAD.mantle, 302), 0, 0, COL.mantle, { seed: 302, depth: 0.7, boil: false });
      paperPiece(tornCircle(R * RAD.outer, 303), 0, 0, COL.outerCore, { seed: 303, depth: 0.7, boil: false });
      paperPiece(tornCircle(R * RAD.inner, 304), 0, 0, COL.innerCore, { seed: 304, depth: 0.6, boil: false });
      // a little engraving hatch on the mantle so it reads as rock, not liquid
      g.save(); g.beginPath(); g.arc(0, 0, R * RAD.mantle - 4, 0, TAU); g.arc(0, 0, R * RAD.outer + 4, 0, TAU, true); g.clip();
      g.strokeStyle = 'rgba(80,30,10,0.16)'; g.lineWidth = 1.4; g.beginPath();
      for (let i = -R; i < R; i += 11) { g.moveTo(i, -R); g.lineTo(i + R * 0.6, R); }
      g.stroke(); g.restore();
    });
    g.restore();
  }
  /* layer label with a leader line; x,y = text anchor; (px, py) = the point on the layer */
  function leader(str, x, y, px, py, t0, o = {}) {
    if (T < t0) return;
    const a = o.alpha ?? 1; if (a <= 0) return;
    g.save(); g.globalAlpha *= a;
    ink([[px, py], [lerp(px, x, 0.6), lerp(py, y, 0.6) + 10], [x + (o.align === 'right' ? 12 : -12), y + 4]], { w: 4, color: o.color || PAL.cream, seed: o.seed || 1, prog: P(T, t0, t0 + 0.35), step: 12, passes: 1 });
    hand(str, x, y, o.size || 46, t0 + 0.25, { align: o.align || 'left', color: o.color || PAL.cream, outline: 'rgba(20,24,40,0.8)', seed: (o.seed || 1) + 1 });
    if (o.sub) hand(o.sub, x, y + (o.size || 46) * 0.95, (o.size || 46) * 0.78, o.subT ?? t0 + 0.6, { align: o.align || 'left', color: o.subColor || PAL.mustard, outline: 'rgba(20,24,40,0.8)', seed: (o.seed || 1) + 2 });
    g.restore();
  }
  function s3Open(t, c) { return E.inOutCubic(P(t, c.W('s3_02', 'slice') + 0.35, c.W('s3_02', 'slice') + 1.35)); }
  function s3Earth(t, c, { open = 1, x = X3.x, y = X3.y, R = X3.R } = {}) {
    if (open < 1) {
      // the globe, with a knife line; then its halves slide apart and fade, the cut face shows
      const d = open * (R + 40), a = 1 - P(open, 0.45, 1);
      if (open > 0) { g.save(); g.beginPath(); g.rect(x - d, y - R - 30, 2 * d, 2 * R + 60); g.clip(); xsection(x, y, R); g.restore(); }
      [[-1, x - R - 30, 0], [1, x, 0]].forEach(([sgn, cx0]) => {
        g.save(); g.globalAlpha *= a; g.beginPath(); g.rect(sgn < 0 ? x - R - 40 - d : x + d, y - R - 40, R + 40, 2 * R + 80); g.clip();
        g.translate(sgn * d, 0);
        g.translate(x, y); g.scale(R / G1.R, R / G1.R); globe(0, 0, G1.R, {});
        g.restore();
      });
      const tk = c.W('s3_02', 'slice');
      if (T > tk && open < 0.3) ink([[x + 8, y - R - 50], [x - 4, y + R + 50]], { w: 6, color: PAL.cream, seed: 310, prog: P(T, tk, tk + 0.35), passes: 2 });
      return;
    }
    xsection(x, y, R);
  }
  function s3Labels(t, c, part, { R = X3.R, x = X3.x, y = X3.y, alpha = 1 } = {}) {
    const tC = c.W('s3_02', 'slice') + 1.4, reply = part.kind === 'reply', after = part.kind === 'after';
    const id = replyId(part);
    leader('crust (really much thinner)', x + R * 0.42 + 190, y - R - 18, x + R * 0.45, y - R * 0.88, tC, { seed: 320, size: 40, alpha });
    const mantleSub = reply ? (id === 'q3_right' ? c.W(id, 'Solid') : c.W(id, 'solid')) : after ? -1e6 : 1e6;
    leader('mantle', x + R + 60, y - R * 0.34, x + R * 0.8, y - R * 0.35, tC + 0.5, { seed: 322, alpha,
      sub: t >= mantleSub ? 'solid rock that slowly flows' : '?', subT: t >= mantleSub ? mantleSub : tC + 0.9 });
    if (reply || after) {
      const tF = reply ? (id === 'q3_wrong2' ? 1e6 : c.W(id, 'creeps')) : -1e6;
      if (t > tF) hand('creeps a few cm a year', x + R + 60, y - R * 0.34 + 88, 34, tF, { align: 'left', color: PAL.cream, outline: 'rgba(20,24,40,0.8)', seed: 325, alpha });
    }
    if (after) {
      const tO = c.W('s3_03', 'outer'), tL = c.W('s3_03', 'liquid'), tI = c.W('s3_04', 'inner'), tS = c.W('s3_04', 'six');
      leader('outer core', x - R - 70, y + R * 0.05, x - R * 0.42, y + R * 0.05, tO, { align: 'right', seed: 330, alpha, sub: 'liquid metal', subT: tL });
      leader('inner core', x - R - 70, y + R * 0.52, x - R * 0.08, y + R * 0.1, tI, { align: 'right', seed: 333, alpha, sub: 'solid, about ' + fmtInt(Plates.INNER_CORE_C) + ' °C', subT: tS });
      const tSun = c.W('s3_04', 'Sun');
      if (t > tSun && alpha > 0) {
        const s = pop(t, tSun, 0.4), sx = x - R - 420, sy = y + R * 0.52 + 30;
        g.save(); g.globalAlpha *= alpha; g.translate(sx, sy); g.scale(s, s);
        for (let k = 0; k < 10; k++) { const a = k / 10 * TAU + t * 0.2; ink([[Math.cos(a) * 44, Math.sin(a) * 44], [Math.cos(a) * 62, Math.sin(a) * 62]], { w: 5, color: PAL.mustard, seed: 336 + k, step: 10, passes: 1 }); }
        paperCircle(0, 0, 36, PAL.mustard, { seed: 347, depth: 0.6 });
        g.restore();
        hand('the Sun', sx, sy + 86, 32, tSun + 0.3, { color: PAL.mustard, outline: 'rgba(20,24,40,0.8)', seed: 348, alpha });
      }
    }
  }
  /* the liquid outer core: slow ink swirls (drawn live, pure in t) */
  function swirl(t, x, y, R, a) {
    if (a <= 0 || LOW()) return;
    g.save(); g.globalAlpha *= a;
    for (let k = 0; k < 5; k++) {
      const r = R * lerp(RAD.inner + 0.05, RAD.outer - 0.04, k / 4), a0 = t * (0.5 + k * 0.08) + k * 1.3, pts = [];
      for (let i = 0; i <= 14; i++) { const u = a0 + i / 14 * 1.3; pts.push([x + Math.cos(u) * r, y + Math.sin(u) * r]); }
      ink(pts, { w: 3, color: 'rgba(160,80,10,0.55)', seed: 340 + k, step: 12, passes: 1 });
    }
    g.restore();
  }
  /* s3 after, from s3_05: the zoom box — top of the mantle, a ridge and a sinking plate edge */
  const ZB = { x: 1250, y: 440, w: 980, h: 560 };
  function zoomBox(t, c, s) {
    if (s <= 0) return;
    const { x, y, w, h } = ZB;
    g.save(); g.translate(x, y); g.scale(s, s); g.translate(-x, -y);
    paperRect(x, y, w + 26, h + 26, PAL.white, { seed: 350, depth: 1.6 });
    g.save(); g.beginPath(); g.rect(x - w / 2, y - h / 2, w, h); g.clip();
    const top = y - h / 2 + 90, ridgeX = x - w / 2 + 250, trenchX = x + w / 2 - 250;
    // water, mantle
    g.fillStyle = paperTile('#9cc7cf', 2); g.fillRect(x - w / 2, y - h / 2, w, 130);
    g.fillStyle = paperTile(COL.mantle, 3); g.fillRect(x - w / 2, top + 20, w, h);
    // heat escaping from below (wavy lines drifting up)
    const tHeat = c.W('s3_06', 'Heat');
    if (t > tHeat && !LOW()) {
      g.save(); g.globalAlpha *= P(t, tHeat, tHeat + 0.6) * 0.6;
      for (let k = 0; k < 7; k++) { const px = x - w / 2 + 80 + k * 135, ph = ((t * 40 + k * 53) % 180); const pts = []; for (let i = 0; i <= 6; i++) pts.push([px + Math.sin(i * 1.2 + k) * 10, y + h / 2 - ph - i * 16]); ink(pts, { w: 3, color: PAL.red, seed: 360 + k, step: 10, passes: 1 }); }
      g.restore();
    }
    // the moving plate: from the ridge to the trench, then bending down (marks slide along it once it's "pulled")
    const tPull = c.W('s3_07', 'pull'), mv = t > tPull ? (t - tPull) * 26 : 0;
    const slab = [];
    for (let i = 0; i <= 40; i++) { const u = i / 40, sx = lerp(ridgeX + 6, trenchX, u); slab.push([sx, top]); }
    for (let i = 1; i <= 12; i++) { const a = i / 12 * 0.75; slab.push([trenchX + Math.sin(a) * 60 * i / 2, top + (1 - Math.cos(a)) * 60 * i / 2 + i * 14]); }
    const thick = 34;
    const upper = slab, lower = slab.map(([px, py], i) => [px, py + thick]).reverse();
    paperPiece([...upper, ...lower], 0, 0, COL.basalt, { seed: 370, depth: 0.8, boil: false });
    // stripes that move with the plate (toward the trench)
    g.save(); pathPts([...upper, ...lower]); g.clip(); g.strokeStyle = 'rgba(251,245,230,0.35)'; g.lineWidth = 4;
    g.beginPath(); for (let k = 0; k < 12; k++) { const px = ridgeX + ((k * 60 + mv) % 720); g.moveTo(px, top); g.lineTo(px - 10, top + thick); } g.stroke(); g.restore();
    // the other plate (left of the ridge) and the overriding plate (right of the trench)
    paperPiece([[x - w / 2 - 10, top], [ridgeX - 6, top], [ridgeX - 6, top + thick], [x - w / 2 - 10, top + thick]], 0, 0, COL.basalt, { seed: 371, depth: 0.8, boil: false });
    paperPiece([[trenchX + 30, top - 30], [x + w / 2 + 10, top - 40], [x + w / 2 + 10, top + 60], [trenchX + 110, top + 58]], 0, 0, COL.crust, { seed: 372, depth: 1.0, boil: false });
    // the ridge bump
    paperPiece([[ridgeX - 46, top + 2], [ridgeX - 12, top - 26], [ridgeX + 12, top - 26], [ridgeX + 46, top + 2]], 0, 0, COL.basalt, { seed: 373, depth: 0.6, boil: false });
    g.restore();
    // arrows: hot rock rising at the ridge; the cold edge sinking and pulling
    const tHot = c.W('s3_06', 'Hot'), tCold = c.W('s3_07', 'cold');
    inkArrow(ridgeX, y + h / 2 - 40, ridgeX, top + 60, { w: 9, color: PAL.red, seed: 380, prog: P(t, tHot, tHot + 0.8), head: 30 });
    inkArrow(ridgeX - 10, top + 70, ridgeX - 170, top + 110, { bulge: -40, w: 7, color: PAL.red, seed: 381, prog: P(t, tHot + 0.6, tHot + 1.2) });
    inkArrow(ridgeX + 10, top + 70, ridgeX + 170, top + 110, { bulge: 40, w: 7, color: PAL.red, seed: 382, prog: P(t, tHot + 0.6, tHot + 1.2) });
    if (t > tHot) label('hot rock rises', ridgeX + 30, top + 190, 42, tHot + 0.4, { align: 'left', color: PAL.cream, outline: 'rgba(20,24,40,0.8)', seed: 383 });
    inkArrow(trenchX + 70, top + 90, trenchX + 150, y + h / 2 - 50, { bulge: -30, w: 9, color: PAL.navy, seed: 384, prog: P(t, tCold, tCold + 0.8), head: 30 });
    if (t > tCold) label('cold, heavy edge sinks', trenchX + 40, y + h / 2 - 110, 40, tCold + 0.4, { align: 'right', color: PAL.cream, outline: 'rgba(20,24,40,0.8)', seed: 385 });
    if (t > tPull) { paperArrow(x - 150, top - 42, 220 * pop(t, tPull, 0.4), 30, PAL.mustard, { seed: 386 }); label('pulls the plate along', x + 110, top - 42, 38, tPull + 0.3, { align: 'left', seed: 387 }); }
    hand('the top of the mantle, zoomed in', x, y + h / 2 + 44, 34, -1e6, { dur: 0.01, color: PAL.cream, outline: 'rgba(20,24,40,0.8)', seed: 388 });
    g.restore();
  }
  function s3Draw(t, part, ctx, c) {
    spaceBackdrop();
    const after = part.kind === 'after', reply = part.kind === 'reply', id = replyId(part);
    const open = part.kind === 'lines' ? s3Open(t, c) : 1;
    const t5 = c.L('s3_05'), mv = after ? E.inOutCubic(P(t, t5 - 0.2, t5 + 1.0)) : 0;
    const x = lerp(X3.x, 440, mv), y = lerp(X3.y, 470, mv), R = X3.R, sc = lerp(1, 0.78, mv);
    g.save(); g.translate(x, y); g.scale(sc, sc); g.translate(-X3.x, -X3.y);
    s3Earth(t, c, { open });
    if (after) swirl(t, X3.x, X3.y, R, P(t, c.W('s3_03', 'outer'), c.W('s3_03', 'outer') + 0.6) * (1 - mv));
    if (open >= 1) s3Labels(t, c, part, { alpha: 1 - P(mv, 0, 0.4) });
    // a volcano bump + magma pockets (q3_wrong0 "only in a few places, like under volcanoes")
    if (reply && id === 'q3_wrong0') {
      const tv = c.W(id, 'volcanoes'), s = pop(t, tv, 0.4), a = -1.15, vx = X3.x + Math.cos(a) * R, vy = X3.y + Math.sin(a) * R;
      if (s > 0) { g.save(); g.translate(vx, vy); g.rotate(a + Math.PI / 2); g.scale(s, s);
        paperPiece([[-40, 6], [-8, -40], [8, -40], [40, 6]], 0, 0, COL.basalt, { seed: 390, depth: 0.8 });
        paperCircle(0, -40, 9, COL.magma, { seed: 391, depth: 0.3 }); paperCircle(-6, 22, 11, COL.magma, { seed: 392, depth: 0.3 }); paperCircle(10, 40, 8, COL.magma, { seed: 393, depth: 0.3 });
        g.restore(); }
    }
    // the little marker on the Earth where the zoom box looks (after s3_05)
    if (after && mv > 0) { g.save(); g.globalAlpha *= mv; g.strokeStyle = PAL.cream; g.lineWidth = 5 / sc; g.strokeRect(X3.x + 60, X3.y - R - 14, 150, 70); g.restore(); }
    g.restore();
    // q3_wrong0: the volcano engraving (assets/img/volcano.png) taped beside the Earth: "melted rock: only in a few places"
    if (reply && id === 'q3_wrong0' && IMG.volcano) {
      const tv = c.W(id, 'volcanoes'), s = pop(t, tv + 0.2, 0.4);
      if (s > 0) {
        cut('volcano', 340, 560, 280, { s, seed: 397, depth: 1.2, rot: -0.03, boil: false });
        if (s > 0.9) { tape(340 - 170, 560 - 125, 90, -0.6, undefined, 398); tape(340 + 170, 560 - 125, 90, 0.6, undefined, 399); }
        tag('melted rock: only in a few places', 340, 745, 36, pop(t, tv + 0.5, 0.35), { color: PAL.white, seed: 400 });
      }
    }
    // Leo's guess (lines): LAVA OCEAN?
    if (part.kind === 'lines') {
      const tl = c.W('s3_01', 'lava'), s = pop(t, tl, 0.35) * unpop(t, c.W('s3_02', 'slice'));
      if (s > 0) { g.save(); g.translate(560, 190); g.rotate(-0.08); g.scale(s, s); paperRect(0, 0, 330, 100, COL.magma, { seed: 395, depth: 1.2 }); g.restore(); ransom('LAVA?', 560, 190, 60 * Math.max(0.01, s), tl, { seed: 396 }); }
    }
    if (reply) {
      // the wrong ideas, crossed out
      const bad = id === 'q3_wrong0' ? ['A SEA OF LAVA', c.W(id, 'Not')] : id === 'q3_wrong2' ? ['HOLLOW', c.W(id, 'terrible')] : null;
      if (bad) { tag(bad[0], 1480, 640, 48, pop(t, 0.5, 0.35), { font: TYPE, color: PAL.white, seed: 397 }); strike(1300, 650, 1660, 630, P(t, bad[1], bad[1] + 0.35), 398); }
      if (id === 'q3_wrong2' && t > c.W(id, 'solid')) label('solid rock all the way down', 1480, 740, 42, c.W(id, 'solid'), { color: PAL.cream, outline: 'rgba(20,24,40,0.8)', seed: 399 });
      if (id === 'q3_right') {
        const tt = c.W(id, 'toothpaste'), s = pop(t, tt, 0.4);
        if (s > 0) {
          g.save(); g.translate(1500, 660); g.rotate(-0.2); g.scale(s, s);
          paperPiece([[-110, -34], [60, -40], [90, -16], [90, 16], [60, 40], [-110, 34]], 0, 0, PAL.white, { seed: 401, depth: 1 });
          paperRect(110, 0, 40, 36, PAL.red, { seed: 402, depth: 0.6 });
          const L = 30 + 90 * P(t, tt, tt + 4);
          paperRect(130 + L / 2, 4, L, 22, COL.mantle, { seed: 403, depth: 0.4 });
          g.restore();
          label('the slowest toothpaste', 1500, 760, 38, tt + 0.3, { color: PAL.cream, outline: 'rgba(20,24,40,0.8)', seed: 404 });
        }
      }
      answerNote(ctx, part, 250, 170, 0.35, 0.9, { w: 340 });
    }
    if (after) zoomBox(t, c, E.outBack(P(t, t5 + 0.3, t5 + 0.9), 1.4));
    if (after && mv > 0) {
      // leader lines from the marker to the zoom box
      const s = P(t, t5 + 0.6, t5 + 1.0);
      if (s > 0) { g.save(); g.globalAlpha *= s; g.setLineDash([10, 8]); g.strokeStyle = PAL.cream; g.lineWidth = 3;
        const mx = x + (60 + 150) * sc, my0 = y + (-R - 14) * sc, my1 = y + (-R + 56) * sc;
        g.beginPath(); g.moveTo(mx, my0); g.lineTo(ZB.x - ZB.w / 2, ZB.y - ZB.h / 2); g.moveTo(mx, my1); g.lineTo(ZB.x - ZB.w / 2, ZB.y + ZB.h / 2); g.stroke(); g.restore(); }
    }
  }
  S.s3 = {
    get timing() { return { lead: { lines: CARD.lead }, hold: { lines: 1.4, reply: 1.2, after: 1.2 }, gaps: { s3_02: 0.7, s3_05: 0.9, s3_06: 0.5 } }; },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      if (part.kind === 'lines') out.push({ name: 'whoosh', t: c.W('s3_02', 'slice'), gain: 0.45, rate: 1.4 }, { name: 'tear', t: c.W('s3_02', 'slice') + 0.35, gain: 0.45 }, { name: 'pop', t: c.W('s3_01', 'lava'), gain: 0.35 }, { name: 'lava', t: c.W('s3_01', 'lava'), gain: 0.3, dur: 2 });
      else if (part.kind === 'reply') {
        const id = replyId(part);
        out.push({ name: 'scribble', t: id === 'q3_right' ? c.W(id, 'Solid') : c.W(id, 'solid'), gain: 0.35 });
        if (id !== 'q3_right') out.push({ name: 'scribble', t: id === 'q3_wrong0' ? c.W(id, 'Not') : c.W(id, 'terrible'), gain: 0.4 });
        if (id === 'q3_right') out.push({ name: 'boing', t: c.W(id, 'toothpaste'), gain: 0.3, rate: 0.6 });
      } else {
        out.push({ name: 'scribble', t: c.W('s3_03', 'outer'), gain: 0.3 }, { name: 'scribble', t: c.W('s3_04', 'inner'), gain: 0.3 }, { name: 'sparkle', t: c.W('s3_04', 'Sun'), gain: 0.35 });
        out.push({ name: 'whoosh', t: c.L('s3_05') - 0.2, gain: 0.35 }, { name: 'lava', t: c.W('s3_06', 'Hot'), gain: 0.25, dur: 3 }, { name: 'grind', t: c.W('s3_07', 'pull'), gain: 0.25, loop: true, dur: 3 });
      }
      return out;
    },
    draw(t, part, ctx) {
      const c = cues(ctx);
      s3Draw(t, part, ctx, c);
      const st = stage(t, part, [[0, 'inset']]);
      cast(t, ctx, st, { quinn: part.kind === 'lines' && t > c.W('s3_02', 'slice') - 0.4 ? 'point' : part.kind === 'after' && t > c.L('s3_06') ? 'point' : null });
      chapter(t, part);
    },
  };

  /* ======================================================================
     s4 — "When plates meet": apart / together / slide; India into Asia; the Himalaya; Iceland and San Andreas
     ====================================================================== */
  /* a little cross-section card of one motion (x, y = centre); p = 0..1 how far the motion has gone */
  function motionCard(kind, x, y, s, p, seed) {
    if (s <= 0) return;
    const w = 440, h = 300;
    g.save(); g.translate(x, y); g.scale(s, s);
    paperRect(0, 0, w, h, PAL.white, { seed, depth: 1.3 });
    g.save(); g.beginPath(); g.rect(-w / 2 + 10, -h / 2 + 10, w - 20, h - 20); g.clip();
    if (kind === 'slide') {
      // seen from above: two blocks sliding past along a fault
      const d = 40 * p;
      paperRect(-100, -d, 180, 220, COL.crust, { seed: seed + 1, depth: 0.5 }); paperRect(100, d, 180, 220, '#a8845c', { seed: seed + 2, depth: 0.5 });
      ink([[0, -130], [0, 130]], { w: 4, color: PLATE_INK, seed: seed + 3, step: 10 });
      paperArrow(-100, 20, 90, 22, PAL.red, { rot: -Math.PI / 2, seed: seed + 4 }); paperArrow(100, -20, 90, 22, PAL.red, { rot: Math.PI / 2, seed: seed + 5 });
    } else {
      const apart = kind === 'apart', d = (apart ? 1 : -1) * 34 * p;
      g.fillStyle = paperTile(COL.mantle, 3); g.fillRect(-w / 2, 20, w, h);
      if (apart) { paperRect(0, 0, 40 + 2 * d, 70, COL.magma, { seed: seed + 6, depth: 0.2 }); }
      paperRect(-110 - d, -10, 200, 60, COL.crust, { seed: seed + 1, depth: 0.7 });
      paperRect(110 + d, -10, 200, 60, '#a8845c', { seed: seed + 2, depth: 0.7 });
      if (!apart && p > 0) paperPiece([[-30, -38], [-12, -38 - 50 * p], [4, -38 - 30 * p], [18, -38 - 60 * p], [34, -38]], 0, 0, COL.crust, { seed: seed + 7, depth: 0.5 });
      paperArrow(apart ? -80 : -190, -80, 90, 22, PAL.red, { rot: apart ? Math.PI : 0, seed: seed + 4 });
      paperArrow(apart ? 80 : 190, -80, 90, 22, PAL.red, { rot: apart ? 0 : Math.PI, seed: seed + 5 });
    }
    g.restore();
    const L = { apart: 'pull apart', together: 'push together', slide: 'slide past' }[kind];
    hand(L, 0, h / 2 - 32, 44, -1e6, { dur: 0.01, color: PAL.navy, outline: 'rgba(251,245,230,0.95)', seed: seed + 8 });
    g.restore();
  }
  function s4Cards(t, c, strip) {
    const ts = [c.W('s4_01', 'apart'), c.W('s4_01', 'together'), c.W('s4_01', 'slide')];
    ['apart', 'together', 'slide'].forEach((k, i) => {
      const x = lerp(520 + i * 440, 700 + i * 260, strip), y = lerp(340, 110, strip), s = pop(t, ts[i] - 0.2, 0.4) * lerp(1, 0.5, strip);
      motionCard(k, x, y, s, E.inOutSine(P(t, ts[i], ts[i] + 1.2)), 410 + i * 20);
    });
  }
  /* South Asia scrap: India (the data's own coastline, cut out) creeping north into Asia */
  const INDIA_V = { x: 960, y: 540, w: 1000, h: 560, s: 17, lon0: 80, lat0: 22 };
  let INDIA = null;
  function indiaPath() {
    if (INDIA) return INDIA;
    const G = geo(); if (!G) return null;
    const ring = G.rings.find(r => r.box.x0 < -10 && r.box.x1 > 170 && r.box.y1 > 70);       // Afro-Eurasia
    if (!ring) return null;
    const inPen = q => q[0] > 66.3 && q[0] < 92.2 && q[1] < 24.6;
    // the longest contiguous run of the ring inside the peninsula box: Pakistan → Cape Comorin → Bangladesh
    let best = [], cur = [];
    for (const q of ring.pts.concat(ring.pts)) { if (inPen(q)) cur.push(q); else { if (cur.length > best.length) best = cur; cur = []; } if (cur.length > ring.pts.length) break; }
    if (best.length < 5) return null;
    // closed along an approximate Himalayan front (drawing only: the plate's northern edge)
    const north = [[92, 26.5], [88, 27.2], [84, 28.3], [80, 29.8], [76, 32], [73, 33.5], [70.5, 30.5], [67.5, 26]];
    const a = best[0], b = best[best.length - 1];
    const poly = (a[0] > b[0] ? best.slice().reverse() : best).concat(north);
    const p = new Path2D(); poly.forEach((q, k) => (k ? p.lineTo(q[0], -q[1]) : p.moveTo(q[0], -q[1]))); p.closePath();
    INDIA = { path: p, poly };
    return INDIA;
  }
  function indiaScrap(t, c, { x = INDIA_V.x, y = INDIA_V.y, s = 1, creep = 1 } = {}) {
    if (s <= 0) return;
    const IN = indiaPath(), v = INDIA_V;
    g.save(); g.translate(x, y); g.scale(s, s); g.translate(-v.x, -v.y);
    layer('india', v.x - v.w / 2 - 20, v.y - v.h / 2 - 20, v.w + 40, v.h + 40, () => {
      GroundLook.survey(v.x, v.y, v.w, v.h, { seed: 31, depth: 1.4, grid: false, contour: 0.3 });
      geoBase(v, { erase: IN ? { path: IN.path, color: '#d3e0dc' } : null });
    });
    if (IN) {
      const dy = (1 - creep) * 4.5;                      // degrees south of today's place
      g.save(); clipBox(v); intoView(v); g.translate(0, dy);
      g.fillStyle = paperTile(PAL.kraft, 6, 'tex_kraft'); g.fill(IN.path);
      g.lineWidth = 2.2 / v.s; g.strokeStyle = 'rgba(43,36,32,0.8)'; g.stroke(IN.path);
      g.restore();
    }
    platesNamed(v, /^(IN-EU|EU-IN)$/, { w: 5, dash: [16, 10], alpha: 0.9 });
    hand('ASIA', vX(v, 95), vY(v, 33), 56, -1e6, { dur: 0.01, color: PAL.navy, font: TYPE, seed: 440 });
    hand('INDIA', vX(v, 77), vY(v, 21 - (1 - creep) * 4.5), 50, -1e6, { dur: 0.01, color: PAL.ink, font: TYPE, seed: 441 });
    g.restore();
  }
  /* the crumple: a cross-section of India (left) driving into Asia (right); the middle crumples up by scaling */
  const CR = { x: 1210, y: 520, w: 900, h: 420 };
  const CRUMPLE = (() => { const r = rng(450), pts = [[-170, 0]]; for (let i = 1; i < 12; i++) pts.push([-170 + i * 30, -(i % 2 ? 150 + r() * 70 : 60 + r() * 40) * Math.sin(i / 12 * Math.PI)]); pts.push([170, 0]); pts.k = 'crumple'; return pts; })();
  function crumple(t, t0, s) {
    if (s <= 0) return;
    const { x, y, w, h } = CR, cr = E.outCubic(P(t, t0, t0 + 2.2)), push = 50 * cr;
    g.save(); g.translate(x, y); g.scale(s, s);
    paperRect(0, 0, w, h, PAL.white, { seed: 451, depth: 1.4 });
    g.save(); g.beginPath(); g.rect(-w / 2 + 10, -h / 2 + 10, w - 20, h - 20); g.clip();
    g.fillStyle = paperTile(COL.mantle, 3); g.fillRect(-w / 2, 90, w, h);
    const himg = IMG.himalaya;
    if (himg && cr > 0.4) cut('himalaya', 0, 40, 260 * cr, { ay: 1, alpha: P(cr, 0.4, 0.8), depth: 0.6, seed: 452 });
    paperPiece(CRUMPLE, 0, 12, COL.crust, { sy: Math.max(0.02, cr), seed: 453, depth: 0.8 });
    paperRect(0, 50, 240, 90, COL.crust, { seed: 449, depth: 0.4 });
    paperRect(-280 + push, 50, 460, 90, COL.crust, { seed: 454, depth: 0.8 });
    paperRect(280 - push, 50, 460, 90, '#a8845c', { seed: 455, depth: 0.8 });
    // folded strata lines in the crumple (scaled with it)
    g.save(); g.translate(0, 12); g.scale(1, Math.max(0.02, cr)); for (let k = 0; k < 3; k++) { const pts = CRUMPLE.slice(1, -1).map(([px, py]) => [px * 0.9, py * (0.8 - k * 0.22) + 8]); ink(pts, { w: 3, color: 'rgba(80,50,20,0.5)', seed: 456 + k, step: 12, passes: 1 }); } g.restore();
    g.restore();
    paperArrow(-w / 2 + 40, 50, 120, 30, PAL.red, { seed: 457 }); paperArrow(w / 2 - 40, 50, 120, 30, PAL.red, { rot: Math.PI, seed: 458 });
    hand('India', -270 + push, 50, 42, -1e6, { dur: 0.01, color: PAL.cream, seed: 459 }); hand('Asia', 270 - push, 50, 42, -1e6, { dur: 0.01, color: PAL.cream, seed: 460 });
    g.restore();
  }
  /* small map scraps for Iceland (apart) and California (slide) */
  const ICE_V = { x: 560, y: 390, w: 700, h: 440, s: 34, lon0: -18.8, lat0: 64.9 };
  const CAL_V = { x: 1360, y: 390, w: 700, h: 440, s: 40, lon0: -119, lat0: 36.3 };
  function scrap(key, v, s, draw) {
    if (s <= 0) return;
    g.save(); g.translate(v.x, v.y); g.scale(s, s); g.translate(-v.x, -v.y);
    layer(key, v.x - v.w / 2 - 20, v.y - v.h / 2 - 20, v.w + 40, v.h + 40, () => {
      GroundLook.survey(v.x, v.y, v.w, v.h, { seed: key.length * 7, depth: 1.4, grid: false, contour: 0.3 });
      geoBase(v, { coastW: 2.4 });
    });
    draw();
    g.restore();
  }
  function s4After(t, c) {
    const G = geo();
    const tI = c.W('s4_04', 'Iceland'), tS = c.W('s4_04', 'Slide'), tSA = c.W('s4_04', 'San');
    scrap('iceland', ICE_V, pop(t, c.W('s4_04', 'Pull') - 0.2, 0.4), () => {
      platesNamed(ICE_V, /^(EU-NA|NA-EU)$/, { w: 6, dash: [16, 10] });
      const s = pop(t, c.W('s4_04', 'apart'), 0.35), a = Math.sin(t * 1.4) * 4;
      paperArrow(vX(ICE_V, -22) - a, vY(ICE_V, 63.2), 110, 28, PAL.red, { rot: Math.PI + 0.45, s, seed: 470 });
      paperArrow(vX(ICE_V, -16.5) + a, vY(ICE_V, 63.0), 110, 28, PAL.red, { rot: 0.45, s, seed: 471 });
      hand('Iceland: pulling apart', ICE_V.x, ICE_V.y - ICE_V.h / 2 + 44, 42, tI, { color: PAL.navy, outline: 'rgba(251,245,230,0.95)', seed: 472 });
      if (t > c.W('s4_04', 'sea')) tag('new sea floor, ' + Plates.EXAMPLES.iceland.say, ICE_V.x, ICE_V.y + ICE_V.h / 2 - 50, 36, pop(t, c.W('s4_04', 'sea'), 0.35), { color: PAL.mustard, seed: 473 });
    });
    scrap('california', CAL_V, pop(t, tS - 0.2, 0.4), () => {
      platesNamed(CAL_V, /^(NA-PA|PA-NA)$/, { w: 6, dash: [16, 10] });
      // the real 2025 M5+ quakes in the scrap
      if (G) quakeDots(CAL_V, G.q.filter(r => r[0] > -128 && r[0] < -110 && r[1] > 30 && r[1] < 43), { k: 2.2, pop: () => pop(t, c.W('s4_04', 'earthquakes'), 0.4) });
      const s = pop(t, tSA, 0.35), a = Math.sin(t * 1.4) * 4;
      paperArrow(vX(CAL_V, -122.6), vY(CAL_V, 35.2) + a, 120, 28, PAL.red, { rot: -Math.PI / 2 - 0.65, s, seed: 480 });
      paperArrow(vX(CAL_V, -117.4), vY(CAL_V, 37.6) - a, 120, 28, PAL.red, { rot: Math.PI / 2 - 0.65, s, seed: 481 });
      hand('San Andreas Fault: sliding past', CAL_V.x, CAL_V.y - CAL_V.h / 2 + 44, 42, tSA, { color: PAL.navy, outline: 'rgba(251,245,230,0.95)', seed: 482 });
      if (t > tSA + 0.6) tag('the plates: about ' + fmtNum(Plates.EXAMPLES.sanAndreas.rate) + ' cm a year', CAL_V.x + 70, CAL_V.y + CAL_V.h / 2 - 50, 36, pop(t, tSA + 0.6, 0.35), { color: PAL.mustard, seed: 483 });
    });
    // s4_05: mountains, volcanoes, earthquakes: all plates bumping
    const icons = [['mountains', c.W('s4_05', 'mountains')], ['volcanoes', c.W('s4_05', 'volcanoes')], ['earthquakes', c.W('s4_05', 'earthquakes')]];
    icons.forEach(([k, t0], i) => {
      const s = pop(t, t0, 0.35); if (s <= 0) return;
      const x = 560 + i * 230, y = 700;
      g.save(); g.translate(x, y); g.scale(s, s);
      paperCircle(0, 0, 62, PAL.white, { seed: 490 + i, depth: 1.1 });
      if (i === 0) paperPiece([[-44, 26], [-14, -30], [4, -4], [20, -34], [46, 26]], 0, 0, COL.crust, { seed: 493, depth: 0.4 });
      if (i === 1) { paperPiece([[-44, 26], [-12, -26], [12, -26], [44, 26]], 0, 0, COL.basalt, { seed: 494, depth: 0.4 }); paperCircle(0, -30, 12, COL.magma, { seed: 495, depth: 0.2 }); }
      if (i === 2) { const pts = starPts(40, 7, 0.5); pathPts(pts); g.fillStyle = PAL.mustard; g.fill(); ink(pts, { w: 3, seed: 496, step: 8 }); }
      g.restore();
      hand(k, x, y + 92, 38, t0, { color: PAL.navy, seed: 497 + i });
    });
    const tB = c.W('s4_05', 'bumping');
    if (t > tB) hand('= plates bumping into each other', 1130, 700, 44, tB, { align: 'left', color: PAL.red, seed: 500 });
    const tBuild = c.W('s4_06', 'build');
    if (t > tBuild) UI.stamp(stepTitle('boundary').toUpperCase() + ': NEXT', 1400, 830, { s: 0.6 * pop(t, tBuild, 0.3), rot: -0.06 });
  }
  function s4Draw(t, part, ctx, c) {
    surveyBackdrop(4);
    const id = replyId(part);
    if (part.kind === 'lines') {
      const t2 = c.L('s4_02'), strip = E.inOutCubic(P(t, t2 - 0.4, t2 + 0.5));
      s4Cards(t, c, strip);
      const s = pop(t, t2 + 0.3, 0.45), creep = E.inOutSine(P(t, t2 + 0.5, c.W('s4_02', 'Asia') + 1.2));
      indiaScrap(t, c, { s, creep, y: 580 });
      const tF = c.W('s4_02', 'four');
      if (t > tF) tag('India: about ' + fmtNum(Plates.EXAMPLES.himalaya.rate) + ' cm a year north', 1320, 390, 42, pop(t, tF, 0.35), { color: PAL.mustard, seed: 510 });
      if (t > tF) paperArrow(vX(INDIA_V, 78.5), vY(INDIA_V, 13) + 80, 150 * pop(t, tF, 0.4), 34, PAL.red, { rot: -Math.PI / 2, seed: 511 });
      const tC = c.W('s4_02', 'continents');
      if (t > tC) { tag('continent', 700, 700, 36, pop(t, tC, 0.3), { seed: 512 }); tag('continent', 1250, 470, 36, pop(t, tC + 0.25, 0.3), { seed: 513 }); }
      const tN = c.W('s4_02', 'sink');
      if (t > tN) label('neither one sinks', 700, 780, 40, tN, { color: PAL.red, seed: 514 });
      return;
    }
    if (part.kind === 'reply') {
      indiaScrap(t, c, { x: 470, y: 470, s: 0.58 });
      const tC = id === 'q4_right' ? c.W(id, 'crumples') : c.W(id, 'crumple');
      if (id !== 'q4_right') {
        const t0 = id === 'q4_wrong0' ? c.W(id, 'ocean') : c.W(id, 'trench');
        const a = unpop(t, tC - 0.2, 0.3);
        if (a > 0) indexCard(1210, 360, 760, 200, [
          { s: id === 'q4_wrong0' ? 'volcanoes: an ocean plate sinks' : 'a trench: an ocean plate dives under', t0: t0, size: 40 },
          { s: 'two continents: neither one sinks', t0: t0 + 1.2, size: 40, color: PAL.red },
        ], { seed: 520, alpha: a, s: pop(t, t0 - 0.2, 0.35) });
      }
      crumple(t, tC, pop(t, tC - 0.4, 0.4));
      const right = id === 'q4_right', tE = right ? c.W(id, 'Everest') : tC + 1.6, tR = right ? c.W(id, 'rising') : 1e6;
      if (t > tE) tag('Everest: about ' + fmtInt(Plates.EVEREST_M) + ' m', 1210, 810 - 60, 42, pop(t, tE, 0.35), { color: PAL.white, seed: 521 });
      if (t > tR) label('still rising a few mm a year', 1210, 830, 38, tR, { color: PAL.red, seed: 522 });
      if (id === 'q4_right') answerNote(ctx, part, 1600, 170, 0.35, 0.9, { w: 320, rot: 0.03 });
      else answerNote(ctx, part, 470, 790 - 40, 0.35, 0.9, { w: 340 });
      return;
    }
    s4After(t, c);
  }
  S.s4 = {
    get timing() { return { lead: { lines: CARD.lead }, hold: { lines: 1.3, reply: 1.6, after: 1.3 }, gaps: { s4_02: 1.0, s4_05: 0.8 } }; },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      if (part.kind === 'lines') {
        ['apart', 'together', 'slide'].forEach(w => out.push({ name: 'paper', t: c.W('s4_01', w) - 0.2, gain: 0.35 }));
        out.push({ name: 'whoosh', t: c.L('s4_02'), gain: 0.35 }, { name: 'grind', t: c.L('s4_02') + 0.6, gain: 0.25, loop: true, dur: 3 }, { name: 'pop', t: c.W('s4_02', 'four'), gain: 0.35 });
      } else if (part.kind === 'reply') {
        const id = replyId(part), tC = id === 'q4_right' ? c.W(id, 'crumples') : c.W(id, 'crumple');
        out.push({ name: 'crack', t: tC, gain: 0.4 }, { name: 'grind', t: tC, gain: 0.35, loop: true, dur: 2.2 }, { name: 'ding', t: id === 'q4_right' ? c.W(id, 'Everest') : tC + 1.6, gain: 0.35 });
      } else {
        out.push({ name: 'pop', t: c.W('s4_04', 'Pull') - 0.2, gain: 0.35 }, { name: 'pop', t: c.W('s4_04', 'Slide') - 0.2, gain: 0.35 }, { name: 'quake', t: c.W('s4_04', 'earthquakes'), gain: 0.3, dur: 1.5 });
        ['mountains', 'volcanoes', 'earthquakes'].forEach(w => out.push({ name: 'pop', t: c.W('s4_05', w), gain: 0.3 }));
        out.push({ name: 'stamp', t: c.W('s4_06', 'build'), gain: 0.5 });
      }
      return out;
    },
    draw(t, part, ctx, c = cues(ctx)) {
      s4Draw(t, part, ctx, c);
      const st = stage(t, part, [[0, 'inset']]);
      cast(t, ctx, st, { quinn: part.kind === 'lines' && t > c.L('s4_01') && t < c.E('s4_02') ? 'point' : null });
      chapter(t, part);
    },
  };

  /* ======================================================================
     s5 — "Deep time": rulers across the Atlantic; Pangaea; the Florida column; the sinkhole; granite → sandstone
     ====================================================================== */
  /* A: the Atlantic, schematic: the Americas' coast left, Africa's right; the widening grows as rulers laid end to end */
  const ATL = { x0: 360, x1: 1560, y: 580 };
  function s5Atlantic(t, c) {
    surveyBackdrop(5);
    const pxKm = (ATL.x1 - ATL.x0) / Plates.ATLANTIC_WIDTH_KM;
    const t2 = c.W('s5_02', 'two'), tH = c.W('s5_02', 'hundred'), tE = c.E('s5_02') + 1.2, t3 = c.W('s5_03', 'four');
    const age = Plates.ATLANTIC_AGE_MYR, myr = age * E.inOutSine(P(t, tH, tE));
    // ocean + two torn coasts
    g.fillStyle = paperTile('#a9c9d6', 2); g.fillRect(ATL.x0 - 20, 390, ATL.x1 - ATL.x0 + 40, 380);
    paperPiece([[-500, -190], [0, -190], [16, -110], [-10, -30], [20, 50], [-6, 130], [10, 190], [-500, 190]], ATL.x0, 580, '#e3cf9b', { seed: 530, depth: 1.4 });
    paperPiece([[500, -190], [0, -190], [-18, -120], [8, -50], [-14, 30], [12, 120], [-8, 190], [500, 190]], ATL.x1, 580, '#dcc596', { seed: 531, depth: 1.4 });
    hand('the Americas', ATL.x0 - 180, 450, 42, -1e6, { dur: 0.01, color: PAL.navy, seed: 532, rot: -0.04 });
    hand('Africa', ATL.x1 + 150, 450, 42, -1e6, { dur: 0.01, color: PAL.navy, seed: 533, rot: 0.03 });
    // s5_01: the one ruler (your whole life), big, then it shrinks to where the chain starts
    const one = E.inOutCubic(P(t, tH - 0.6, tH + 0.2));
    if (one < 1) { ruler(lerp(960, ATL.x0 + 10, one), lerp(560, ATL.y, one), lerp(700, 8, one), 0, { seed: 534 }); if (one < 0.3) label('one ruler: your whole life', 960, 670, 44, -1e6, { alpha: 1 - one / 0.3, seed: 535 }); }
    // the chain of rulers: its length is Plates.distance(rate, time) on the map's km scale
    const km = Plates.distance(Plates.ATLANTIC, myr), L = km * pxKm;
    if (L > 1) {
      const RL = 60, n = Math.ceil(L / RL);
      g.save(); g.beginPath(); g.rect(ATL.x0, ATL.y - 40, L, 80); g.clip();
      for (let i = 0; i < n; i++) ruler(ATL.x0 + RL / 2 + i * RL, ATL.y + (i % 2 ? -2 : 2), RL - 2, (i % 3 - 1) * 0.02, { seed: 540 + (i % 5) });
      g.restore();
      hand(fmtInt(km) + ' km', ATL.x0 + L / 2, ATL.y - 60, 48, tH, { dur: 0.01, color: PAL.red, seed: 546, outline: 'rgba(251,245,230,0.9)' });
    }
    // the deep-time clock
    const cs = pop(t, t2 - 0.2, 0.4);
    if (cs > 0) {
      g.save(); g.translate(230, 160); g.scale(cs, cs);
      paperCircle(0, 0, 96, PAL.cream, { seed: 550, depth: 1.3, outline: true });
      for (let k = 0; k < 12; k++) { const a = k / 12 * TAU; ink([[Math.cos(a) * 76, Math.sin(a) * 76], [Math.cos(a) * 88, Math.sin(a) * 88]], { w: 3, seed: 551 + k, step: 10, passes: 1 }); }
      const a = -Math.PI / 2 + myr * 0.35;
      ink([[0, 0], [Math.cos(a) * 70, Math.sin(a) * 70]], { w: 6, color: PAL.red, seed: 565, step: 10 });
      ink([[0, 0], [Math.cos(a / 12) * 46, Math.sin(a / 12) * 46]], { w: 8, seed: 566, step: 10 });
      g.restore();
      hand(fmtInt(myr) + ' million years', 230, 300, 40, -1e6, { dur: 0.01, color: PAL.navy, seed: 567 });
    }
    // the pencil working (every number from Plates)
    const years = age * 1e6, cm = Math.round(Plates.ATLANTIC * years), kmF = Math.round(Plates.distance(Plates.ATLANTIC, age));
    if (t > t2 - 0.3) indexCard(1110, 185, 1180, 250, [
      { s: fmtNum(Plates.ATLANTIC) + ' cm a year × ' + fmtInt(years) + ' years', t0: t2, size: 46 },
      { s: '= ' + fmtInt(cm) + ' cm = ' + fmtInt(kmF) + ' km', t0: t3, size: 50, color: PAL.red, dy: 6 },
      { s: '(1 cm a year for 1 million years = ' + Plates.KM_PER_CM_YR_MYR + ' km)', t0: t3 + 1.0, size: 32, dy: 10, color: 'rgba(43,36,32,0.7)' },
    ], { seed: 570, s: pop(t, t2 - 0.3, 0.35), rot: 0.008 });
    // "roughly the width of the Atlantic": today's width, measured across (Plates.ATLANTIC_WIDTH_KM)
    const tW = c.W('s5_03', 'width');
    if (t > tW) {
      const p = P(t, tW, tW + 0.6);
      g.save(); g.globalAlpha *= p; g.setLineDash([12, 9]); g.strokeStyle = PAL.navy; g.lineWidth = 4;
      g.beginPath(); g.moveTo(ATL.x0, 690); g.lineTo(ATL.x1, 690); g.moveTo(ATL.x0, 670); g.lineTo(ATL.x0, 710); g.moveTo(ATL.x1, 670); g.lineTo(ATL.x1, 710); g.stroke(); g.restore();
      hand('the Atlantic today: about ' + fmtInt(Plates.ATLANTIC_WIDTH_KM) + ' km', 960, 730, 40, tW + 0.2, { color: PAL.navy, seed: 575 });
    }
  }
  /* B: Pangaea (a simplified sketch: the Americas and Greenland slid back against Africa and Europe) */
  const PG_V = { x: 960, y: 425, w: 1560, h: 720, s: 5.0, lon0: -22, lat0: 8 };
  // rigid 2-D moves in map degrees (hand-fitted on the screenshots; the label says "simplified")
  const PANGAEA = { SA: { dx: 39, dy: 8, rot: -0.75, px: -35, py: -6 }, NA: { dx: 52, dy: -4, rot: 0.2, px: -75, py: 35 } };   // Greenland rides with NA
  function s5Pangaea(t, c) {
    surveyBackdrop(6);
    const G = geo(), v = PG_V;
    layer('pangaea_paper', v.x - v.w / 2 - 20, v.y - v.h / 2 - 20, v.w + 40, v.h + 40, () => GroundLook.survey(v.x, v.y, v.w, v.h, { seed: 41, depth: 1.4, grid: false, contour: 0.3 }));
    if (!G) return;
    const tS = c.W('s5_05', 'Stuck'), p = E.inOutCubic(P(t, tS - 0.3, tS + 2.4));
    g.save(); clipBox(v);
    g.fillStyle = 'rgba(79,127,163,0.16)'; g.fillRect(v.x - v.w / 2, v.y - v.h / 2, v.w, v.h);
    intoView(v);
    g.lineJoin = 'round';
    g.fillStyle = paperTile('#dcc596', 5, 'tex_kraft'); g.fill(G.rest); g.lineWidth = 1.6 / v.s; g.strokeStyle = LAND_EDGE; g.stroke(G.rest);
    [['SA', '#e3cf9b'], ['NA', '#e6d3a2']].forEach(([k, col]) => {
      if (!G.pieces[k].length) return;
      const m = PANGAEA[k];
      g.save(); g.translate(m.px + m.dx * p, -(m.py + m.dy * p)); g.rotate(m.rot * p); g.translate(-m.px, m.py);
      const path = GroundVideo.path(k, G.pieces[k]);
      g.fillStyle = paperTile(col, 4, 'tex_kraft'); g.fill(path); g.lineWidth = 1.8 / v.s; g.strokeStyle = 'rgba(43,36,32,0.8)'; g.stroke(path);
      g.restore();
    });
    g.restore();
    const tP = c.W('s5_05', 'Pangaea');
    if (t > tP) tag('Pangaea (a simplified sketch)', 960, 110, 50, pop(t, tP, 0.35), { color: PAL.mustard, seed: 580, font: HANDF });
    const tSp = c.W('s5_05', 'splitting');
    if (t > tSp) tag('started splitting about ' + fmtInt(Plates.PANGAEA_SPLIT_MYR) + ' million years ago', 960, 760, 40, pop(t, tSp, 0.35), { color: PAL.white, seed: 581 });
    const tA = c.W('s5_04', 'Africa');
    if (t > tA && p <= 0.01) label('?', 900, 420, 120, tA, { color: PAL.red, seed: 582 });
  }
  /* C: the Florida column: limestone on top, deep down a chunk of old Africa */
  const FL_V = { x: 560, y: 400, w: 660, h: 500, s: 52, lon0: -83, lat0: 27.6 };
  const COLM = { x: 1330, top: 170, w: 380, h: 640 };
  function shellDoodle(x, y, r, seed) { const pts = []; for (let i = 0; i <= 26; i++) { const a = i / 26 * TAU * 2.2, rr = r * (0.15 + 0.85 * i / 26); pts.push([x + Math.cos(a) * rr, y + Math.sin(a) * rr * 0.8]); } ink(pts, { w: 3, color: 'rgba(90,70,40,0.75)', seed, step: 6, passes: 1 }); }
  function column(t, { shells = 0, africa = 1, x = COLM.x, top = COLM.top, w = COLM.w, h = COLM.h } = {}) {
    // limestone (top), older layers, and the old-Africa basement slid in from the right
    paperRect(x, top + h * 0.2, w, h * 0.4, COL.limestone, { seed: 590, torn: true, depth: 1.2 });
    paperRect(x, top + h * 0.5, w, h * 0.22, COL.sandstone, { seed: 591, torn: true, depth: 0.6 });
    paperRect(x, top + h * 0.64, w, h * 0.12, COL.shale, { seed: 592, torn: true, depth: 0.6 });
    const ax = lerp(x + 700, x, E.outCubic(africa));
    if (africa > 0) paperRect(ax, top + h * 0.86, w, h * 0.3, PAL.kraft, { seed: 593, torn: true, depth: 1.2, tex: 'tex_kraft' });
    if (shells > 0) { const r = rng(594); for (let i = 0; i < 9; i++) { const sx = x - w / 2 + 40 + r() * (w - 80), sy = top + 30 + r() * (h * 0.36); if (shells * 9 > i) shellDoodle(sx, sy, 14 + r() * 8, 595 + i); } }
    hand('limestone', x, top + h * 0.2, 44, -1e6, { dur: 0.01, color: PAL.navy, seed: 596, font: TYPE });
    hand('older layers', x, top + h * 0.53, 36, -1e6, { dur: 0.01, color: PAL.ink, seed: 597 });
    if (africa > 0.6) hand('old Africa', ax, top + h * 0.86, 50, -1e6, { dur: 0.01, color: PAL.cream, outline: 'rgba(43,36,32,0.8)', seed: 598, font: TYPE });
  }
  function s5Florida(t, c) {
    surveyBackdrop(7);
    scrap('florida', FL_V, 1, () => {
      const pin = [vX(FL_V, -81.8), vY(FL_V, 28.4)];
      paperCircle(pin[0], pin[1], 14, PAL.red, { seed: 600, depth: 0.8 });
      hand('Florida', vX(FL_V, -85.6), vY(FL_V, 27.6), 50, -1e6, { dur: 0.01, color: PAL.navy, font: TYPE, seed: 601 });
    });
    const pin = [vX(FL_V, -81.8), vY(FL_V, 28.4)];
    // a short dig arrow down from the pin, then a dashed zoom line over to the column (what's under the pin)
    inkArrow(pin[0], pin[1] + 20, pin[0], pin[1] + 120, { w: 5, color: PAL.navy, seed: 602, head: 20 });
    g.save(); g.setLineDash([14, 12]); g.lineWidth = 3; g.strokeStyle = 'rgba(31,45,78,0.7)'; g.beginPath();
    g.moveTo(pin[0] + 18, pin[1] + 60); g.lineTo(COLM.x - COLM.w / 2 - 20, COLM.top + COLM.h * 0.2); g.stroke(); g.restore();
    hand('dig straight down', pin[0] - 20, pin[1] + 160, 36, -1e6, { dur: 0.01, color: PAL.navy, seed: 603, rot: -0.04, outline: 'rgba(251,245,230,0.95)' });
    column(t, { africa: P(t, c.W('s5_06', 'Africa') - 0.3, c.W('s5_06', 'Africa') + 0.6) });
    const tS = c.W('s5_07', 'standing');
    if (t > tS) tag('you are standing on it', COLM.x, COLM.top + COLM.h + 30, 40, pop(t, tS, 0.35), { color: PAL.mustard, seed: 604 });
  }
  /* D: limestone → sinkhole: rain + carbon dioxide dissolves it, a cavity grows, the ground drops in */
  const SK = { x: 960, y: 450, w: 1300, h: 620 };
  const CAVITY = blobPts(180, 110, 610, 0.08, 40);
  function s5Sinkhole(t, c) {
    surveyBackdrop(8);
    const { x, y, w, h } = SK, surf = y - h / 2 + 150;
    const tR = c.W('s5_09', 'Rain'), tD = c.W('s5_09', 'dissolves'), tK = c.W('s5_09', 'sinkholes'), tSh = c.W('s5_08', 'shells');
    paperRect(x, y, w + 34, h + 34, PAL.white, { seed: 609, depth: 1.5 });
    g.save(); g.beginPath(); g.rect(x - w / 2, y - h / 2, w, h); g.clip();
    g.fillStyle = paperTile('#cfe4ea', 3); g.fillRect(x - w / 2, y - h / 2, w, 170);
    paperRect(x, surf + (h - 150) / 2 + 10, w + 40, h - 150 + 20, COL.limestone, { seed: 611, torn: true, depth: 1.2 });
    const r = rng(612); for (let i = 0; i < 16; i++) { const sx = x - w / 2 + 60 + r() * (w - 120), sy = surf + 60 + r() * (h - 240); if (t > tSh + i * 0.12) shellDoodle(sx, sy, 14 + r() * 10, 613 + i); }
    // the cavity: one cached blob, scaled as it dissolves
    const grow = E.outCubic(P(t, tD, tK)), cx = x + 60, cy = surf + 150;
    if (grow > 0) paperPiece(CAVITY, cx, cy, '#6b6358', { s: 0.15 + 0.85 * grow, seed: 614, depth: 0.2 });
    // the surface (sand + grass); over the cavity it drops in after "sinkholes"
    const drop = E.inQuad(P(t, tK, tK + 0.7));
    paperRect(x - 360, surf - 12, w / 2 + 100, 44, '#e2c48e', { seed: 615, torn: true, depth: 0.8, tex: 'tex_kraft' });
    paperRect(x + 460, surf - 12, w / 2 - 220, 44, '#e2c48e', { seed: 616, torn: true, depth: 0.8, tex: 'tex_kraft' });
    paperRect(cx, surf - 12 + drop * 150, 360, 44, '#e2c48e', { seed: 617, torn: true, depth: 0.8, tex: 'tex_kraft', rot: drop * 0.25 });
    [[x - 520, 0], [x - 200, 1], [x + 560, 2]].forEach(([gx, k]) => paperPiece([[-30, 0], [-10, -40], [0, -8], [12, -44], [30, 0]], gx, surf - 30, PAL.green, { seed: 618 + k, depth: 0.4 }));
    // rain (pure in t)
    if (t > tR) {
      g.save(); g.globalAlpha *= P(t, tR, tR + 0.4) * (1 - P(t, tK + 1.5, tK + 2.5)); g.fillStyle = COL.ocean;
      for (let i = 0; i < 26; i++) { const rx = x - w / 2 + 40 + (i * 97) % (w - 80), ry = y - h / 2 + ((t * 420 + i * 61) % 160); g.beginPath(); g.ellipse(rx, ry, 5, 11, 0, 0, TAU); g.fill(); }
      g.restore();
    }
    g.restore();
    const tC = c.W('s5_09', 'carbon');
    if (t > tC) tag('rain + carbon dioxide = slightly acidic', x - 250, y - h / 2 + 60, 40, pop(t, tC, 0.35), { color: PAL.white, seed: 620 });
    if (t > tSh) tag('shells of ancient sea creatures', x - 330, y + h / 2 - 70, 38, pop(t, tSh, 0.35), { color: PAL.mustard, seed: 621 });
    if (t > tK + 0.4) UI.stamp('SINKHOLE', cx + 330, surf + 120, { s: 0.6 * pop(t, tK + 0.4, 0.3), rot: 0.08 });
  }
  /* E: granite countertop → sand → sandstone ("any kind of rock can become any other kind") */
  const GRAINS = (() => { const r = rng(630), out = []; for (let i = 0; i < 34; i++) out.push({ sx: -130 + r() * 260, sy: -40 + r() * 80, px: (r() - 0.5) * 240 * (1 - Math.abs(r() - 0.5)), py: 50 - r() * 60, r: 8 + r() * 7, c: r() < 0.3 ? '#e8d4c8' : r() < 0.5 ? '#8b8580' : '#d9c7a8' }); out.forEach(o => { o.py = 60 - Math.max(0, 70 - Math.abs(o.px) * 0.5) * (0.4 + 0.6 * Math.abs(o.py) / 60); }); return out; })();
  function rockBlock(name, x, y, w, h, color, seed, o = {}) {
    if (IMG[name]) { cut(name, x, y, h * 1.3, { seed, depth: 1.2, alpha: o.alpha ?? 1, s: o.s ?? 1 }); return; }
    if ((o.s ?? 1) <= 0 || (o.alpha ?? 1) <= 0) return;
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.scale(o.s ?? 1, o.s ?? 1);
    paperRect(0, 0, w, h, color, { seed, depth: 1.3, outline: true });
    // stand-in texture: granite = dark and pink crystals; sandstone = fine pale sand grains
    const sandy = name === 'sandstone', r = rng(seed + 1);
    for (let i = 0; i < (sandy ? 60 : 30); i++) paperCircle(-w / 2 + 14 + r() * (w - 28), -h / 2 + 12 + r() * (h - 24), sandy ? 1.5 + r() * 2 : 3 + r() * 5,
      sandy ? (i % 2 ? '#f3e2bd' : '#b68f5e') : i % 3 ? 'rgba(60,50,45,0.7)' : '#e9b9a5', { seed: seed + 10 + i, depth: 0 });
    g.restore();
  }
  function s5Granite(t, c) {
    surveyBackdrop(9);
    const tG = c.W('s5_11', 'granite'), tSa = c.W('s5_11', 'sand'), tSs = c.W('s5_11', 'sandstone'), tAny = c.W('s5_11', 'Any');
    const X = [470, 960, 1450], Y = 440;
    const crumble = E.inOutCubic(P(t, tSa - 0.2, tSa + 1.0)), press = E.inOutCubic(P(t, tSs - 0.2, tSs + 0.9));
    rockBlock('granite', X[0], Y, 300, 130, COL.granite, 640, { s: pop(t, tG - 0.4, 0.4), alpha: 1 - 0.65 * crumble });
    hand('granite countertop', X[0], Y + 130, 42, tG, { color: PAL.navy, seed: 641 });
    // grains fly from the slab to a pile; then the pile presses into a block
    if (crumble > 0) GRAINS.forEach((o, i) => {
      const e = E.inOutCubic(clamp(crumble * 1.3 - i / GRAINS.length * 0.3)), gx = lerp(X[0] + o.sx, X[1] + o.px, e), gy = lerp(Y + o.sy, Y + o.py, e) - Math.sin(e * Math.PI) * 60;
      paperCircle(gx, gy, o.r, o.c, { seed: 650 + i, depth: 0.3 });
    });
    // once the grains have landed, the sand specimen (assets/img/sand.png) settles over the pile
    if (IMG.sand && t > tSa + 0.5) cut('sand', X[1], Y + 20, 150, { seed: 651, depth: 1, alpha: P(t, tSa + 0.5, tSa + 1.0), s: 1 - 0.12 * press });
    if (t > tSa + 0.6) hand('sand', X[1], Y + 130, 44, tSa + 0.6, { color: PAL.navy, seed: 642, alpha: 1 });
    if (press > 0) {
      paperArrow(X[2], Y - 190, 80, 30, PAL.red, { rot: Math.PI / 2, s: press * (1 - P(t, tSs + 1.4, tSs + 1.8)), seed: 643 });
      rockBlock('sandstone', X[2], Y, 280, 120, COL.sandstone, 644, { s: pop(t, tSs + 0.3, 0.4) });
      hand('sandstone', X[2], Y + 130, 44, tSs + 0.5, { color: PAL.navy, seed: 645 });
    }
    inkArrow(X[0] + 170, Y - 80, X[1] - 150, Y - 80, { bulge: -40, w: 6, color: PAL.navy, seed: 646, prog: P(t, tSa - 0.2, tSa + 0.4) });
    inkArrow(X[1] + 150, Y - 80, X[2] - 170, Y - 80, { bulge: -40, w: 6, color: PAL.navy, seed: 647, prog: P(t, tSs - 0.2, tSs + 0.4) });
    if (t > tSa) hand('weathered, broken down', (X[0] + X[1]) / 2, Y - 170, 34, tSa, { color: PAL.ink, seed: 648 });
    if (t > tSs) hand('pressed and cemented', (X[1] + X[2]) / 2, Y - 170, 34, tSs, { color: PAL.ink, seed: 649 });
    if (t > tAny) hand('any kind of rock can become any other kind', 960, 740, 52, tAny, { color: PAL.red, seed: 660 });
    const tT = c.W('s5_13', 'take');
    if (t > tT) tag('next stop: ' + stepTitle('rocks'), X[2], Y + 210, 40, pop(t, tT, 0.35), { color: PAL.mustard, seed: 661, rot: 0.06 });
  }
  S.s5 = {
    get timing() { return { lead: { lines: CARD.lead }, hold: { lines: 2.2 }, gaps: { s5_02: 0.8, s5_04: 1.0, s5_06: 1.0, s5_08: 1.0, s5_11: 1.0 } }; },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: 'scribble', t: c.W('s5_02', 'two'), gain: 0.3 }, { name: 'rolling', t: c.W('s5_02', 'hundred'), gain: 0.2, loop: true, dur: 3 }, { name: 'ding', t: c.W('s5_03', 'four'), gain: 0.4 });
      ['s5_04', 's5_06', 's5_08', 's5_11'].forEach(id => out.push({ name: 'tear', t: c.L(id) - 0.5, gain: 0.45 }));
      out.push({ name: 'grind', t: c.W('s5_05', 'Stuck') - 0.3, gain: 0.3, loop: true, dur: 2.6 }, { name: 'thunk', t: c.W('s5_06', 'Africa') + 0.3, gain: 0.45 });
      out.push({ name: 'drip', t: c.W('s5_09', 'Rain'), gain: 0.35, loop: true, dur: 3 }, { name: 'crack', t: c.W('s5_09', 'sinkholes'), gain: 0.45 }, { name: 'thunk', t: c.W('s5_09', 'sinkholes') + 0.6, gain: 0.5 }, { name: 'stamp', t: c.W('s5_09', 'sinkholes') + 0.4, gain: 0.45 });
      out.push({ name: 'crack', t: c.W('s5_11', 'sand') - 0.2, gain: 0.35 }, { name: 'thunk', t: c.W('s5_11', 'sandstone') + 0.4, gain: 0.4 }, { name: 'sparkle', t: c.W('s5_13', 'take'), gain: 0.35 });
      return out;
    },
    draw(t, part, ctx) {
      const c = cues(ctx);
      const k = scenes(t, [[-1e9, u => s5Atlantic(u, c)], [c.L('s5_04') - 0.5, u => s5Pangaea(u, c)], [c.L('s5_06') - 0.5, u => s5Florida(u, c)], [c.L('s5_08') - 0.5, u => s5Sinkhole(u, c)], [c.L('s5_11') - 0.5, u => s5Granite(u, c)]]);
      const st = stage(t, part, [[0, 'inset']]);
      cast(t, ctx, st, { leo: /s5_0[47]/.test(ctx.caption ? ctx.caption.id : '') ? 'point' : null, quinn: k === 0 && t > c.W('s5_02', 'two') && t < c.E('s5_03') ? 'point' : null });
      chapter(t, part);
    },
  };

  /* ======================================================================
     the step: the player, plus pre-baking the heavy layers in spare frame time
     ====================================================================== */
  const _paths = new Map();
  const GroundVideo = window.GroundVideo = {
    layers: LY, geo, ortho, nearestEdge, indiaPath, PANGAEA, MIDDLES, MIAMI,
    _edge: undefined,
    edge() { if (this._edge === undefined) this._edge = nearestEdge(MIAMI[0], MIAMI[1]); return this._edge; },
    path(k, rings) { if (!_paths.has(k)) _paths.set(k, geo().pathOf(rings)); return _paths.get(k); },
  };
  class VideoScene extends Player.Scene {
    enter() {
      super.enter();
      // warm the data and the big static layers while the chapter card is up (each bakes once; prewarm runs only in
      // spare frame time, within the engine's bake budget)
      if (typeof prewarm === 'function') {
        prewarm(() => { geo(); globePaths(); GroundVideo.edge(); });
        prewarm.draw(() => beach());
        prewarm.draw(() => surveyBackdrop(3));
        prewarm.draw(() => worldCard());
      }
    }
  }
  Levels.video = VideoScene;
})();
